import {
  Component,
  OnInit,
  OnDestroy,
  signal,
  inject,
  computed,
  WritableSignal,
  effect,
  untracked,
  ViewChild,
  HostListener,
  input,
  ChangeDetectionStrategy
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { BreakpointObserver } from '@angular/cdk/layout';
import {
  CdkDragDrop,
  DragDropModule,
} from '@angular/cdk/drag-drop';
import {
  applyLayoutEditorAutoWidthUpdates,
  applyLayoutEditorCardDrop,
  computeLayoutEditorAutoWidthUpdates,
  reorderLayoutEditorItemsOnVisibilityToggle,
} from '../../../../shared/components/dashboard/dashboard-layout-editor.helpers';
import { map } from 'rxjs/operators';
import { CommonModule } from '@angular/common';
import { Subscription } from 'rxjs';
import { ApiService } from '../../../../core/services/api.service';
import { apiBaseUrlFor } from '../../../../core/services/prism-new-routing';
import { environment } from '../../../../../environments/environment';
import { CommonService } from '../../../../core/services/common.service';
import { AiBotService } from '../../../../core/services/ai-bot.service';
import { ProductConfigService } from '../../../../core/services/product-config.service';
import {
  IconComponent,
  IconName,
} from '../../../../shared/icons/icon.component';
import { MultiLineChartComponent } from '../../../../shared/components/charts/multi-line-chart/multi-line-chart.component';
import { IndividualToasterComponent } from '../../../../shared/components/loader/loader.component';
import { ThemeService } from '../../../../core/services/theme.service';
import { DashboardCardsComponent } from '../../../../shared/components/ui/dashboard-cards/dashboard-cards.component';
import { ToggleBtnsComponent } from '../../../../shared/components/toggle-btns/toggle-btns.component';
import {
  PaginatedTableComponent,
  TableHeader,
} from '../../../../shared/components/tables/paginated-table/paginated-table.component';
import {
  ReactiveFormsModule,
  FormBuilder,
  FormGroup,
  Validators,
  FormArray,
  AbstractControl,
  ValidationErrors,
  ValidatorFn,
} from '@angular/forms';
import { SlidePanelComponent } from '../../../../shared/components/slide-panel/slide-panel.component';
import { AuditEntityType } from '../../../../core/models/audit-log.model';
import { AuditLogPanelComponent } from '../../../../shared/components/audit-log-panel/audit-log-panel.component';

import { ButtonComponent } from '../../../../shared/components/ui/button/button.component';
import {
  DropdownComponent,
  DropdownOption,
} from '../../../../shared/components/ui/dropdown/dropdown.component';
import { InputComponent } from '../../../../shared/components/ui/input/input.component';
import { AlertComponent } from '../../../../shared/components/alert/alert.component';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { ConfirmationModalComponent } from '../../../../shared/components/confirmation-modal/confirmation-modal.component';
import { ToasterService } from '../../../../core/services/toaster.service';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { PredefinedRange } from '../../../../shared/components/date-picker/date-picker.component';
import { NormalDatePickerComponent } from '../../../../shared/components/simple-date-picker/normal-date-picker.component';
import { ScrollTableComponent } from '../../../../shared/components/tables/scrolled-table/scroll-table.component';
import { FunnelChartComponent } from '../../../../shared/components/charts/funnel-chart/funnel-chart.component';
import { HorizontalBarChartComponent } from '../../../../shared/components/charts/horizontal-bar-chart/horizontal-bar-chart.component';
import { DashboardSectionLayoutService } from '../../../../core/services/dashboard-section-layout.service';
import { DashboardPageKey, SeoStatsSectionId, SeoStatsSectionDef } from '../../../../core/enums/dashboard-cards.enum';
import { DashboardWidgetPickerDialogComponent } from '../../../../shared/components/dashboard/dashboard-widget-picker-dialog.component';
import { AiHeadlinesComponent } from '../../../../shared/components/ai-headlines/ai-headlines.component';
import { CustomDashboardPinService } from '../../../../core/services/custom-dashboard-pin.service';
import { DashboardLayoutService } from '../../../../core/services/dashboard-layout.service';
import { CustomizeLayoutBatchService } from '../../../../core/services/customize-layout-batch.service';
import { createEmbeddedSectionFilter, findCatalogPageId, resolveActiveCatalogProductId, stripProductPrefix } from '../../../../shared/components/dashboard/dashboard-embed.helpers';

dayjs.extend(utc);
dayjs.extend(timezone);

/** Persisted layout state per section (visibility + width + order). */
interface SectionLayoutItem {
  id: SeoStatsSectionId;
  visible: boolean;
  /** 4 | 6 | 8 | 12 — proportional width inside the 12-col dashboard grid. */
  colSpan: number;
  order: number;
}

/** Draft row used by the inline Customize Layout editor.
 *  Carries the section title alongside the persisted layout fields so the
 *  popup tile can render its label without re-looking-up `SECTIONS`. */
interface LayoutEditorItem extends SectionLayoutItem {
  title: string;
}

interface SEOStat {
  traffic: number;
  activeVisitors: number;
  uniqueVisitors: number;
  clicks: number;
  impressions: number;
  trafficChange: number;
  activeVisitorsChange: number;
  uniqueVisitorsChange: number;
  clicksChange: number;
  impressionsChange: number;
}

interface LandingPage {
  id?: string;
  url?: string;
  clicks?: number;
  keyword?: string; // Add optional keyword
  rank?: number;    // Add optional rank
  date?: string;
}

interface TopCity {
  id?: string;
  city: string;
  userCount: number;
  date?: string;
}

interface CountryStat {
  id?: string;
  country: string;
  impressions: number;
  clicks: number;
  date?: string;
}

interface LatestReview {
  id: string;
  reviewDate: string;
  rating: number;
  reviewText: string;
  reviewerName: string;
  platformId: string;
}

interface ReviewRating {
  id?: string;
  platform: string;
  platformId: string | null;
  rating: number;
  reviewsCount: number;
  icon: string;
  latestReviews?: LatestReview[];
}

interface ChartDataPoint {
  date: string;
  value: number;
  start: string;
  end: string;
  label: string;
  key: string;
}
import { TextareaComponent } from '../../../../shared/components/ui/textarea-input/textarea.component';

import { ParallelHorizontalBarChartComponent } from '../../../../shared/components/charts/parallel-horizontal-bar-chart/parallel-horizontal-bar-chart.component';
import { VerticalRankingChartComponent } from '../../../../shared/components/charts/vertical-ranking-chart/vertical-ranking-chart.component';

import { LayoutEditorOverlayDirective } from '../../../../shared/components/dashboard/layout-editor-overlay.directive';
import { IconComponentV2 } from '../../../../shared/components/icon-component/icon.component';

@Component({
  selector: 'app-seo-stats',
  standalone: true,
  imports: [
    CommonModule,
    LayoutEditorOverlayDirective,
    IconComponent,
    MultiLineChartComponent,
    IndividualToasterComponent,
    PaginatedTableComponent,
    SlidePanelComponent,
    ReactiveFormsModule,
    ButtonComponent,
    DropdownComponent,
    InputComponent,
    AlertComponent,
    MatDialogModule,
    NormalDatePickerComponent,
    ScrollTableComponent,
    TextareaComponent,
    HorizontalBarChartComponent,
    ParallelHorizontalBarChartComponent,
    AuditLogPanelComponent,
    IconComponentV2,
    AiHeadlinesComponent,
    DragDropModule,
  ],
  templateUrl: './seo-stats.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styles: [],
})
export class SeoStatsComponent implements OnInit, OnDestroy {
  private apiService = inject(ApiService);
  public commonService = inject(CommonService);
  private aiBotService = inject(AiBotService);
  private productConfigService = inject(ProductConfigService);
  private themeService = inject(ThemeService);
  private breakpointObserver = inject(BreakpointObserver);
  private dialog = inject(MatDialog);
  private toasterService = inject(ToasterService);
  private sectionLayoutService = inject(DashboardSectionLayoutService);
  private customDashboardPin = inject(CustomDashboardPinService);
  private dashboardLayoutService = inject(DashboardLayoutService);
  private _customizeLayoutBatch = inject(CustomizeLayoutBatchService);

  readonly mixedMode = input<boolean>(false);
  readonly visibleWidgetKeys = input<string[] | null>(null);
  readonly catalogPageId = input<number | null>(null);

  readonly effectiveCatalogPageId = signal<number | null>(null);

  readonly PAGE_KEY = DashboardPageKey.SEO_STATS;
  readonly SectionId = SeoStatsSectionId;
  readonly SECTIONS = SeoStatsSectionDef;
  private resolveAgentSubscription?: Subscription;

  /**
   * Section ids that must NEVER appear in the Customize Layout editor or in
   * the drag-drop / Custom Dashboard flow. Audit Log is rendered as a
   * standalone block below the customizable grid, so it's filtered out of
   * `layoutMap`, `editorItems`, and the defaults reset.
   */
  private readonly LAYOUT_EXCLUDED_SECTION_IDS: ReadonlySet<SeoStatsSectionId> =
    new Set<SeoStatsSectionId>([SeoStatsSectionId.AUDIT_LOG]);

  /** SECTIONS minus any section excluded from the customizable layout. */
  private readonly LAYOUT_SECTIONS = SeoStatsSectionDef.filter(
    (s) => !this.LAYOUT_EXCLUDED_SECTION_IDS.has(s.id as SeoStatsSectionId),
  );

  isDarkTheme = signal(false);
  isCompactView = toSignal(
    this.breakpointObserver.observe('(max-height: 620px)').pipe(map(result => result.matches)),
    { initialValue: typeof window !== 'undefined' ? window.innerHeight <= 620 : false }
  );
  isLoading = signal<boolean>(false);
  isOverallLoading = signal<boolean>(false);
  auditEntityTypes = [
    AuditEntityType.SEO_METRIC,
    AuditEntityType.SEO_RATING,
    AuditEntityType.SEO_CITY_USERS,
    AuditEntityType.SEO_KEYWORD_RANK,
    AuditEntityType.SEO_COUNTRY_STATS,
    AuditEntityType.SEO_AI_OVERVIEW_KEYWORD,
    AuditEntityType.SEO_REVIEW
  ];
  detailedReviews = signal<any[]>([]);
  totalDetailedReviews = signal(0);
  detailedReviewsPage = signal(1);
  unit = signal<any>('month');
  selectedDateRange = signal<{ start: string; end: string } | null>(null, {
    equal: (a, b) => a?.start === b?.start && a?.end === b?.end,
  });
  selectedTableDate = signal<{ start: string; end: string } | null>(null);
  selectedTableYear = signal<{ start: string } | null>(null);

  @ViewChild('dateDropdown') dateDropdown!: DropdownComponent;
  @ViewChild(AuditLogPanelComponent) auditLogPanel!: AuditLogPanelComponent;
  LA_TIMEZONE = 'America/Los_Angeles';
  nowInLA = () => dayjs().tz(this.LA_TIMEZONE);
  currentYear = this.nowInLA().year();
  currentMonth = this.nowInLA().month() + 1;


  get selectedStartRange() {
    return this.nowInLA().subtract(89, 'day').startOf('day').toISOString();
  }

  get selectedEndRange() {
    return this.nowInLA().endOf('day').toISOString();
  }

  predefinedRanges: PredefinedRange[] = [
    {
      label: 'Last 90 Days',
      getValue: () => ({
        start: this.nowInLA().subtract(89, 'day').startOf('day').toISOString(),
        end: this.nowInLA().endOf('day').toISOString(),
      }),
    },
    {
      label: 'This Quarter',
      getValue: () => {
        const now = this.nowInLA();
        const quarter = Math.floor(now.month() / 3);
        const quarterStart = now.month(quarter * 3).startOf('month');
        return {
          start: quarterStart.toISOString(),
          end: now.endOf('day').toISOString(),
        };
      },
    },
    {
      label: 'Last Quarter',
      getValue: () => {
        const now = this.nowInLA().subtract(3, 'month');
        const quarter = Math.floor(now.month() / 3);
        const quarterStart = now.month(quarter * 3).startOf('month');
        const quarterEnd = now.month(quarter * 3 + 2).endOf('month');
        return {
          start: quarterStart.toISOString(),
          end: quarterEnd.toISOString(),
        };
      },
    },
    {
      label: 'This Year',
      getValue: () => {
        const now = this.nowInLA();
        return {
          start: now.startOf('year').toISOString(),
          end: now.endOf('day').toISOString(),
        };
      },
    },
    {
      label: 'Last Year',
      getValue: () => {
        const lastYear = this.nowInLA().subtract(1, 'year');
        return {
          start: lastYear.startOf('year').toISOString(),
          end: lastYear.endOf('year').toISOString(),
        };
      },
    },
  ];
  errorMessage = signal<string>('');
  successMessage = signal<string>('');

  // Form Signals
  panelOpen = signal(false);
  formSubmitted = signal(false);
  productsList = signal<DropdownOption[]>([]);
  activeStatsTab = signal<
    'seo-metrics' | 'landing' | 'cities' | 'rating' | 'reviews' | 'ai-overview'
  >('seo-metrics');
  form: FormGroup;
  // Landing Modal Signals
  landingPageForm: FormGroup;
  isLandingPageModalOpen = signal(false);
  currentLandingPageIndex: number | null = null;
  landingPageSubmitted = signal(false);

  // City Modal Signals
  cityForm: FormGroup;
  isCityModalOpen = signal(false);
  currentCityIndex: number | null = null;
  citySubmitted = signal(false);

  // Review Modal Signals
  reviewForm: FormGroup;
  isReviewModalOpen = signal(false);
  currentReviewIndex: number | null = null; // To track if editing and which one
  reviewSubmitted = signal(false);
  productId = signal<string>('');
  isDenefits = computed(() =>
    this.productConfigService.isDenefitsProduct(this.apiService.selectedProductId() || this.productId())
  );
  /** Tags in-flight fetches so stale responses are ignored after product changes. */
  private dataFetchGeneration = 0;
  productPid = computed(() => {
    const id = this.productId();
    const products = this.apiService.products();
    const product = products.find(p => p.id === id);
    return product?.pid || null;
  });

  private fb = inject(FormBuilder);

  // Pagination Signals
  currentPage = signal(1);
  totalItems = signal(0);
  itemsPerPage = 10;
  landingPagesPage = signal(1);
  totalLandingPages = signal(0);
  citiesPage = signal(1);
  totalCityUsers = signal(0);
  countriesPage = signal(1);
  totalCountries = signal(0);
  reviewsPage = signal(1);
  totalReviews = signal(0);
  aiOverviewPage = signal(1);
  totalAiOverview = signal(0);
  aiOverviewData = signal<any[]>([]);
  selectedPlatformId = signal<string | null>(null);
  topReviewsRatings = signal<ReviewRating[]>([]);

  latestReviews = computed(() => {
    const platformId = this.selectedPlatformId();
    const allPlatforms = this.topReviewsRatings();
    if (!platformId) return [];
    const platform = allPlatforms.find((p) => p.platformId === platformId);
    return platform?.latestReviews || [];
  });

  years: DropdownOption[] = this.getFilteredYears();

  getFilteredYears(): DropdownOption[] {
    const currentYear = this.nowInLA().year();
    const years: DropdownOption[] = [];
    for (let y = currentYear; y >= 2016; y--) {
      years.push({ label: y.toString(), value: y.toString() });
    }
    return years;
  }

  allMonths: DropdownOption[] = [
    { label: 'January', value: '1' },
    { label: 'February', value: '2' },
    { label: 'March', value: '3' },
    { label: 'April', value: '4' },
    { label: 'May', value: '5' },
    { label: 'June', value: '6' },
    { label: 'July', value: '7' },
    { label: 'August', value: '8' },
    { label: 'September', value: '9' },
    { label: 'October', value: '10' },
    { label: 'November', value: '11' },
    { label: 'December', value: '12' },
  ];

  private _cachedMonths = new Map<string, DropdownOption[]>();

  getFilteredMonths(yearValue: string | null): DropdownOption[] {
    const key = yearValue || 'all';
    if (this._cachedMonths.has(key)) {
      return this._cachedMonths.get(key)!;
    }

    const now = this.nowInLA();
    const currentYear = now.year();
    const currentMonth = now.month() + 1; // 1-indexed

    let result = this.allMonths;
    if (yearValue && parseInt(yearValue, 10) === currentYear) {
      result = this.allMonths.filter(
        (m) => parseInt(m.value, 10) <= currentMonth
      );
    }

    this._cachedMonths.set(key, result);
    return result;
  }

  get landingPageMonths(): DropdownOption[] {
    return this.getFilteredMonths(this.landingPageForm.get('year')?.value);
  }

  get cityMonths(): DropdownOption[] {
    return this.getFilteredMonths(this.cityForm.get('year')?.value);
  }

  get reviewMonths(): DropdownOption[] {
    return this.getFilteredMonths(this.reviewForm.get('year')?.value);
  }

  platformOptions = signal<DropdownOption[]>([]);
  /** Platform id (the dropdown value) → numeric pid, which prism-new's bulk create requires. */
  private platformPids = new Map<string, number>();

  /** A row's platform as the dropdown value (the platform id); prism-new rows carry only `platformPid`. */
  private platformIdOf(row: any): string | null {
    const id = row?.ratingPlatformId || row?.platformId || row?.platform?.id;
    if (id && this.platformPids.has(String(id))) return String(id);
    const pid = Number(row?.ratingPlatformPid ?? row?.platformPid ?? row?.platform?.pid ?? id);
    for (const [platformId, platformPid] of this.platformPids) if (platformPid === pid) return platformId;
    return id || null;
  }
  countryOptions = signal<DropdownOption[]>([]);
  keywordOptions = signal<DropdownOption[]>([]);
  aiKeywordOptions = signal<DropdownOption[]>([]);

  filteredKeywords = computed(() => {
    const search = this.activeKeywordSearch().toLowerCase().trim();
    if (!search) return [];

    const options = this.activeKeywordTab() === 'ai-overview'
      ? this.aiKeywordOptions()
      : this.keywordOptions();

    return options.filter(opt =>
      opt.label.toLowerCase().includes(search)
    );
  });

  showAutocomplete = computed(() =>
    this.showAutocompleteManual()
  );

  activeKeywordSearch = signal<string>('');
  showAutocompleteManual = signal<boolean>(false);
  activeRowIndex = signal<number | null>(null);
  activeKeywordTab = signal<'landing' | 'ai-overview' | null>(null);


  // View Switching
  private viewSignal: WritableSignal<'analyticsView' | 'tableView'> =
    signal('analyticsView');

  isEditingMetric = signal<boolean>(false);

  get view() {
    return this.viewSignal();
  }

  // SEO Stats
  seoStats = signal<SEOStat>({
    traffic: 14041,
    activeVisitors: 5412,
    uniqueVisitors: 4732,
    clicks: 4830,
    impressions: 1484699,
    trafficChange: 0,
    activeVisitorsChange: 27.5,
    uniqueVisitorsChange: -28.9,
    clicksChange: -46.9,
    impressionsChange: 38.1,
  });

  // Chart data
  clicksChartData = signal<any[]>([
    // ... existing commented out data ...
  ]);

  // Landing pages
  landingPages = signal<LandingPage[]>([]);
  landingPagesSlice = computed(() => this.landingPages().slice(0, 10));

  // Top cities
  topCities = signal<TopCity[]>([]);
  paginatedCities = computed(() => this.topCities());
  topCitiesSlice = computed(() => this.topCities().slice(0, 10));

  // Countries — table view (paginated /seo/country-stats)
  countries = signal<CountryStat[]>([]);
  // Countries — analytics view (from /seo/top-records → topCountryStats)
  topCountryStats = signal<CountryStat[]>([]);
  topAiOverviewKeywords = signal<any[]>([]);

  topKeywordRanking = signal<any[]>([]);

  // Reviews and ratings
  reviewsRatings = signal<ReviewRating[]>([]);
  reviewsRatingsSlice = computed(() => this.reviewsRatings().slice(0, 10));

  // Computed data for Analytics View tables (Paginated Table format)
  landingPageAnalyticsData = computed(() => {
    return this.landingPages().slice(0, 10).map((item, index) => ({
      'S No': index + 1,
      'Keyword': item.keyword,
      'Ranking': item.rank
    }));
  });

  cityAnalyticsData = computed(() => {
    return this.topCities().slice(0, 10).map((item) => ({
      label: item.city,
      value: item.userCount,
      gradientStart: '#8B5CF6',
      gradientEnd: '#6D28D9'
    }));
  });

  cityChartData = computed(() => {
    return this.topCities().slice(0, 10).map((item) => ({
      label: item.city,
      value: item.userCount,
      gradientStart: '#8B5CF6',
      gradientEnd: '#6D28D9'
    }));
  });

  /** Left margin sized to longest city label so y-axis text is not clipped. */
  cityChartLeftMargin = computed(() => {
    const labels = this.cityChartData().map((d) => d.label);
    if (!labels.length) {
      return 120;
    }
    const maxLabelLen = Math.max(...labels.map((l) => l.length));
    return Math.max(120, maxLabelLen * 7 + 24);
  });

  /** Headroom above max traffic so bar value labels fit on the right. */
  cityChartMaxValue = computed(() => {
    const values = this.cityChartData().map((d) => d.value);
    if (!values.length) {
      return 1;
    }
    const maxVal = Math.max(...values, 1);
    return Math.ceil(maxVal * 1.15);
  });

  aiOverviewAnalyticsData = computed(() => {
    return this.topAiOverviewKeywords().slice(0, 10).map((item, index) => ({
      index: index + 1,
      date: item.date,
      keyword: item.keyword,
      action: `<a href="${item.snapshotUrl}" target="_blank" class="text-[#0066CC] hover:underline">View</a>`
    }));
  });

  topKeywordRankingAnalyticsData = computed(() => {
    return this.topKeywordRanking().slice(0, 10).map((item, index) => ({
      index: index + 1,
      date: item.date,
      keyword: item.keyword || 'Unknown',
      rank: item.rank || index + 1
    }));
  });

  countryAnalyticsData = computed(() => {
    const data = this.countries();
    // Sort by clicks descending and take top 10
    const sortedData = [...data].sort((a, b) => (b.clicks || 0) - (a.clicks || 0)).slice(0, 10);
    const maxClicks = Math.max(...sortedData.map(d => d.clicks || 0), 1);

    return sortedData.map(item => ({
      label: item.country,
      value: item.clicks,
      percentage: Math.round(((item.clicks || 0) / maxClicks) * 100),
      amount: item.clicks
    }));
  });

  countryPerformanceStats = computed(() => {
    // Analytics view uses topCountryStats from top-records API — preserve API order.
    const data = this.topCountryStats().slice(0, 10);

    if (data.length === 0) return [];

    const maxImpressions = Math.max(...data.map(d => d.impressions || 0), 1);
    const maxClicks = Math.max(...data.map(d => d.clicks || 0), 1);

    return data.map(item => ({
      country: item.country,
      impressions: item.impressions,
      clicks: item.clicks,
      // Ensure at least a small visible bar if value > 0
      impressionsPercentage: Math.max(((item.impressions || 0) / maxImpressions) * 100, 2),
      clicksPercentage: Math.max(((item.clicks || 0) / maxClicks) * 100, 2)
    }));
  });

  parallelChartData = computed(() => {
    return this.countryPerformanceStats().map(item => ({
      label: item.country,
      leftValue: item.impressions,
      rightValue: item.clicks,
      leftLabelFormatted: item.impressions?.toLocaleString(),
      rightLabelFormatted: item.clicks?.toLocaleString()
    }));
  });

  keywordRankingData = computed(() => {
    return this.topAiOverviewKeywords().slice(0, 10).map((item, index) => ({
      label: item.keyword,
      rank: index + 1
    }));
  });

  topKeywordRankingData = computed(() => {
    return this.topKeywordRanking().slice(0, 10).map((item, index) => ({
      label: item.keyword || 'Unknown',
      rank: item.rank || index + 1
    }));
  });

  // Card stats configuration
  cardStatsConfig = computed(() => {
    const stats = this.seoStats();
    const config = [
      {
        label: 'Traffic',
        value: stats.traffic.toLocaleString(),
        difference: stats.trafficChange,
        bgClass: 'bg-[#6366F1]',
        iconClass: 'text-white dark:text-surface-1',
        icon: 'chartColumn' as IconName,
        shadowClass: 'shadow-[0px_4px_12px_0px_#6366F180]',
        show: true
      },
      {
        label: 'Active Visitors',
        value: stats.activeVisitors.toLocaleString(),
        difference: stats.activeVisitorsChange,
        bgClass: 'bg-[#0C66E8]',
        iconClass: 'text-white dark:text-surface-1',
        icon: 'users' as IconName,
        shadowClass: 'shadow-[0px_4px_12px_0px_#0C66E880]',
        show: stats.activeVisitors > 0
      },
      {
        label: 'Unique Visitors',
        value: stats.uniqueVisitors.toLocaleString(),
        difference: stats.uniqueVisitorsChange,
        bgClass: 'bg-[#AD46FF]',
        iconClass: 'text-white dark:text-surface-1',
        icon: 'userCheck' as IconName,
        shadowClass: 'shadow-[0px_4px_12px_0px_#AD46FF80]',
        show: stats.uniqueVisitors > 0
      },
      {
        label: 'Clicks',
        value: stats.clicks.toLocaleString(),
        difference: stats.clicksChange,
        bgClass: 'bg-[#FFA514]',
        iconClass: 'text-white dark:text-surface-1',
        icon: 'mousePointerClick' as IconName,
        shadowClass: 'shadow-[0px_4px_12px_0px_#FFA51480]',
        show: true
      },
      {
        label: 'Impressions',
        value: stats.impressions.toLocaleString(),
        difference: stats.impressionsChange,
        bgClass: 'bg-[#15B268]',
        iconClass: 'text-white dark:text-surface-1',
        icon: 'eye' as IconName,
        shadowClass: 'shadow-[0px_4px_12px_0px_#15B26880]',
        show: true
      },
    ];

    return config.filter(card => card.show);
  });

  /**
   * Tailwind grid class for the Stats Overview card row.
   *
   * The number of columns on `lg+` adapts to the section's current
   * customize-layout col-span so the cards don't get squeezed when the
   * STATS_OVERVIEW tile is resized via the Customize Layout popup:
   *   - col-12 (Full):   fan out to one column per card (existing 5-up look)
   *   - col-8  (Large):  at most 3 cards per row
   *   - col-6  (Medium): 2 cards per row, remaining wrap to next line
   *   - col-4  (Small):  2 cards per row, remaining wrap to next line
   *
   * Class strings are returned as full literals (not template-built) so
   * Tailwind's source scanner picks them up.
   */
  getStatsGridClass(): string {
    const count = this.cardStatsConfig().length;
    const span = this._normalizeColSpan(
      this.layoutMap()[SeoStatsSectionId.STATS_OVERVIEW]?.colSpan ?? 12,
    );
    const lgCols = this._statsLgColsClass(span, count);
    return `grid grid-cols-1 sm:grid-cols-2 ${lgCols}`;
  }

  getStatsLoaderClass(): string {
    const count = this.cardStatsConfig().length;
    const span = this._normalizeColSpan(
      this.layoutMap()[SeoStatsSectionId.STATS_OVERVIEW]?.colSpan ?? 12,
    );
    const lgSpan = this._statsLgLoaderSpanClass(span, count);
    return `h-[135px] col-span-1 sm:col-span-2 ${lgSpan} flex items-center justify-center bg-slate-50 dark:bg-slate-800/50 rounded-2xl border border-dashed border-slate-200 dark:border-slate-700`;
  }

  /** Picks the `lg:grid-cols-*` class for the Stats Overview row. */
  private _statsLgColsClass(span: number, count: number): string {
    if (span === 4 || span === 6) return 'lg:grid-cols-2';
    if (span === 8) return 'lg:grid-cols-3';
    switch (count) {
      case 1: return 'lg:grid-cols-1';
      case 2: return 'lg:grid-cols-2';
      case 3: return 'lg:grid-cols-3';
      case 4: return 'lg:grid-cols-4';
      case 5: return 'lg:grid-cols-5';
      case 6: return 'lg:grid-cols-6';
      default: return 'lg:grid-cols-5';
    }
  }

  /** Picks the `lg:col-span-*` class for the Stats Overview loader. */
  private _statsLgLoaderSpanClass(span: number, count: number): string {
    if (span === 4 || span === 6) return 'lg:col-span-2';
    if (span === 8) return 'lg:col-span-3';
    switch (count) {
      case 1: return 'lg:col-span-1';
      case 2: return 'lg:col-span-2';
      case 3: return 'lg:col-span-3';
      case 4: return 'lg:col-span-4';
      case 5: return 'lg:col-span-5';
      case 6: return 'lg:col-span-6';
      default: return 'lg:col-span-5';
    }
  }

  /**
   * Tailwind grid class for the Top Reviews & Rating Count cards.
   *
   * Up to 3 review platforms are rendered (`topReviewsRatings().slice(0, 3)`).
   * When the section is resized to the smallest tile (`col-4`), 3-in-a-row
   * squeezes each card too tight, so we drop to 2-per-row and the 3rd card
   * wraps to the next line. All other widths keep the existing 3-up layout.
   *
   * Returned as full class literals so Tailwind's source scanner picks them up.
   */
  getReviewsRatingsGridClass(): string {
    const span = this._normalizeColSpan(
      this.layoutMap()[SeoStatsSectionId.REVIEWS_RATINGS]?.colSpan ?? 12,
    );
    const lgCols = span === 4 ? 'lg:grid-cols-2' : 'lg:grid-cols-3';
    return `grid grid-cols-1 sm:grid-cols-2 ${lgCols} gap-6 [@media(max-height:620px)]:gap-3`;
  }

  /** Matching `col-span` for the Reviews & Ratings empty-state placeholder. */
  getReviewsRatingsEmptySpanClass(): string {
    const span = this._normalizeColSpan(
      this.layoutMap()[SeoStatsSectionId.REVIEWS_RATINGS]?.colSpan ?? 12,
    );
    const lgSpan = span === 4 ? 'lg:col-span-2' : 'lg:col-span-3';
    return `col-span-1 sm:col-span-2 ${lgSpan} flex flex-col items-center justify-center py-12 text-[#7D7D93] dark:text-ink-muted min-h-[200px]`;
  }

  /** Two-column analytics/table grids — stack when Ask AI panel is open. */
  getTwoColumnGridClass(): string {
    return this.commonService.isPanelOpen()
      ? 'grid grid-cols-1'
      : 'grid grid-cols-1 lg:grid-cols-2';
  }

  /** Reviews rating cards — reduce columns when Ask AI panel is open. */
  getReviewsCardsGridClass(): string {
    return this.commonService.isPanelOpen()
      ? 'grid grid-cols-1 sm:grid-cols-2'
      : 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3';
  }

  // Tab Options for View Switching
  // View Options for Dropdown
  viewOptions: DropdownOption[] = [
    {
      label: 'Analytics View',
      value: 'analyticsView',
      icon: 'chartLine',
    },
    {
      label: 'Table View',
      value: 'tableView',
      icon: 'betweenHorizontalStart',
    },
  ];

  landingPageHeaders: TableHeader[] = [
    { label: 'S No', key: 'index', align: 'text-left' },
    {
      label: 'Month & Year',
      key: 'date',
      align: 'text-left',
      dateFormat: 'monthYear',
    },
    { label: 'Keyword', key: 'keyword' },
    { label: 'Ranking', key: 'rank', align: 'text-left' },
  ];

  cityTableHeaders: TableHeader[] = [
    { label: 'S No', key: 'index', align: 'text-left' },
    {
      label: 'Month & Year',
      key: 'date',
      align: 'text-left',
      dateFormat: 'monthYear',
    },
    { label: 'City Name', key: 'city' },
    { label: 'User Count', key: 'userCount', align: 'text-left', transform: 'number' },
  ];

  countryTableHeaders: TableHeader[] = [
    { label: 'S No', key: 'index', align: 'text-left' },
    {
      label: 'Month & Year',
      key: 'date',
      align: 'text-left',
      dateFormat: 'monthYear',
    },
    { label: 'Country', key: 'country' },
    { label: 'Impressions', key: 'impressions', align: 'text-left', transform: 'number' },
    { label: 'Clicks', key: 'clicks', align: 'text-left', transform: 'number' },
  ];
  aiOverviewHeaders: TableHeader[] = [
    { label: 'S No', key: 'index', align: 'text-left' },
    {
      label: 'Month & Year',
      key: 'date',
      align: 'text-left',
      dateFormat: 'monthYear',
    },
    { label: 'AI Overview Keyword', key: 'keyword' },
    { label: 'Snapshot Link', key: 'snapshotLink', isHtml: true },
  ];

  reviewHeaders: TableHeader[] = [
    { label: 'S No', key: 'index', align: 'text-left' },
    {
      label: 'Month & Year',
      key: 'date',
      align: 'text-left',
      dateFormat: 'monthYear',
    },
    { label: 'Platform', key: 'platform' },
    { label: 'Rating', key: 'rating', align: 'text-left', growthKey: 'ratingGrowth' },
    { label: 'Reviews', key: 'reviewsCount', align: 'text-left', transform: 'number' },
  ];

  detailedReviewHeaders: TableHeader[] = [
    { label: 'S No', key: 'index', align: 'text-left' },
    {
      label: 'Date',
      key: 'reviewDate',
      align: 'text-left',
      dateFormat: 'mediumDate',
    },
    { label: 'Rating', key: 'rating', align: 'text-left' },
    { label: 'Review Text', key: 'reviewText', align: 'text-left' },
    { label: 'Reviewer Name', key: 'reviewerName', align: 'text-left' },
  ];

  aiOverviewAnalyticsHeaders: TableHeader[] = [
    { label: 'S No', key: 'index', align: 'text-left' },
    {
      label: 'Month & Year',
      key: 'date',
      align: 'text-left',
      dateFormat: 'monthYear',
    },
    { label: 'AI Overview Keyword', key: 'keyword' },
    { label: 'Snapshot Link', key: 'action' },
  ];

  topKeywordRankingAnalyticsHeaders: TableHeader[] = [
    { label: 'S No', key: 'index', align: 'text-left' },
    {
      label: 'Month & Year',
      key: 'date',
      align: 'text-left',
      dateFormat: 'monthYear',
    },
    { label: 'Keyword', key: 'keyword' },
    { label: 'Rank', key: 'rank', align: 'text-left' },
  ];

  // Analytics View Headers (simplified, no actions)
  landingPageAnalyticsHeaders: TableHeader[] = [
    { label: 'S No', key: 'S No', align: 'text-left' },
    { label: 'Keyword', key: 'Keyword' },
    { label: 'Ranking', key: 'Ranking', align: 'text-center' },
  ];

  cityAnalyticsHeaders: TableHeader[] = [
    { label: 'S No', key: 'S No', align: 'text-left' },
    { label: 'City Name', key: 'City Name' },
    { label: 'User Count', key: 'User Count', align: 'text-center' },
  ];

  reviewAnalyticsHeaders: TableHeader[] = [
    { label: 'Platform', key: 'platform' },
    { label: 'Rating', key: 'rating', align: 'text-center', growthKey: 'ratingGrowth' },
    { label: 'Reviews', key: 'reviewsCount', align: 'text-center' },
  ];

  preventLeadingSpace(event: KeyboardEvent): void {
    const target = event.target as HTMLInputElement;
    if (event.key === ' ' && target.selectionStart === 0) {
      event.preventDefault();
    }
  }

  preventDecimal(event: KeyboardEvent): void {
    if (event.key === '.' || event.key === 'e' || event.key === 'E' || event.key === '+' || event.key === '-') {
      event.preventDefault();
    }
  }

  formatDateToMMYYYY(dateStr: string): string {
    if (!dateStr) return '-';
    const parts = dateStr.split('-');
    if (parts.length >= 2) {
      // YYYY-MM -> MMYYYY
      return parts[1].padStart(2, '0') + parts[0];
    }
    return dateStr;
  }

  // Custom Date Validator
  dateRangeValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.value) return null;

      const now = this.nowInLA();
      const currentYear = now.year();
      const currentMonth = now.month() + 1;

      let year: number | null = null;
      let month: number | null = null;

      if (typeof control.value === 'string') {
        // Handle YYYY-MM-DD
        const parts = control.value.split('-');
        if (parts.length >= 2) {
          year = parseInt(parts[0], 10);
          month = parseInt(parts[1], 10);
        }
      } else if (typeof control.value === 'object' && control.value.year && control.value.month) {
        // Handle { year: number, month: number }
        year = parseInt(control.value.year, 10);
        month = parseInt(control.value.month, 10);
      }

      if (year !== null && month !== null) {
        // Calendar Validity Check
        let isValidCalendarDate = true;
        if (typeof control.value === 'string') {
          // dayjs with strict format for YYYY-MM-DD
          isValidCalendarDate = dayjs(control.value, 'YYYY-MM-DD', true).isValid();
        } else {
          // For object {year, month}, check if month is 1-12
          isValidCalendarDate = month >= 1 && month <= 12;
        }

        if (!isValidCalendarDate) {
          return { dateInvalid: true };
        }

        if (year < 2016) {
          return { dateTooOld: true };
        }
        if (year > currentYear || (year === currentYear && month > currentMonth)) {
          return { dateInFuture: true };
        }
      }

      return null;
    };
  }

  // Static Table Headers
  tableHeaders: TableHeader[] = [
    {
      label: 'Month & Year ',
      key: 'date',
      align: 'text-left',
      dateFormat: 'monthYear',
    },
    {
      label: 'Traffic',
      key: 'traffic',
      align: 'text-left',
      transform: 'number',
    },
    {
      label: 'Clicks',
      key: 'clicks',
      align: 'text-left',
      transform: 'number',
    },
    {
      label: 'Impressions',
      key: 'impressions',
      align: 'text-left',
      transform: 'number',
    },
    {
      label: 'Active Visitors',
      key: 'activeVisitors',
      align: 'text-left',
      transform: 'number',
    },
    {
      label: 'Unique Visitors',
      key: 'uniqueVisitors',
      align: 'text-left',
      transform: 'number',
    },
  ];

  // Dynamic Table Data
  tableData = signal<any[]>([]);

  // FormArrays Getters
  get landingRows(): FormArray {
    return this.form.get('landingRows') as FormArray;
  }
  get cityRows(): FormArray {
    return this.form.get('cityRows') as FormArray;
  }
  get ratingRows(): FormArray {
    return this.form.get('ratingRows') as FormArray;
  }
  get reviewRows(): FormArray {
    return this.form.get('reviewRows') as FormArray;
  }
  get aiOverviewRows(): FormArray {
    return this.form.get('aiOverviewRows') as FormArray;
  }
  get countryRows(): FormArray {
    return this.form.get('countryRows') as FormArray;
  }

  // Row Management Methods
  createLandingRowFormGroup(): FormGroup {
    return this.fb.group({
      landingUrl: ['', [Validators.required]],
      landingClicks: [0, [Validators.required, Validators.min(0), Validators.pattern('^[0-9]*$')]],
    });
  }

  addLandingRow(): void {
    const rows = this.landingRows;
    if (rows && rows.length < 10) {
      rows.push(this.createLandingRowFormGroup());
    }
  }

  removeLandingRow(index: number): void {
    if (this.landingRows.length > 1) {
      this.landingRows.removeAt(index);
    }
  }

  createCityRowFormGroup(): FormGroup {
    return this.fb.group({
      cityName: ['', Validators.required],
      cityUserCount: [0, [Validators.required, Validators.min(0)]],
    });
  }

  addCityRow(): void {
    const rows = this.cityRows;
    if (rows && rows.length < 10) {
      rows.push(this.createCityRowFormGroup());
    }
  }

  removeCityRow(index: number): void {
    if (this.cityRows.length > 1) {
      this.cityRows.removeAt(index);
    }
  }

  createRatingRowFormGroup(): FormGroup {
    return this.fb.group({
      ratingPlatform: ['', Validators.required],
      ratingValue: [null, [Validators.required, Validators.min(0), Validators.max(5)]],
      ratingCount: [0, [Validators.required, Validators.min(0)]],
    });
  }

  addRatingRow(): void {
    const rows = this.ratingRows;
    if (rows && rows.length < 10) {
      rows.push(this.createRatingRowFormGroup());
    }
  }

  removeRatingRow(index: number): void {
    if (this.ratingRows.length > 1) {
      this.ratingRows.removeAt(index);
    }
  }

  createReviewRowFormGroup(): FormGroup {
    return this.fb.group({
      reviewPlatform: ['', Validators.required],
      reviewDate: ['', [Validators.required, this.dateRangeValidator()]],
      reviewRating: [null, [Validators.required, Validators.min(0), Validators.max(5)]],
      reviewText: ['', Validators.required],
      reviewerName: ['', Validators.required],
      reviewerEmail: ['', [Validators.email]],
    });
  }

  addReviewRow(): void {
    const rows = this.reviewRows;
    if (rows && rows.length < 10) {
      rows.push(this.createReviewRowFormGroup());
    }
  }

  removeReviewRow(index: number): void {
    if (this.reviewRows.length > 1) {
      this.reviewRows.removeAt(index);
    }
  }

  createAiOverviewRowFormGroup(): FormGroup {
    return this.fb.group({
      snapshotLink: ['', [Validators.required, Validators.pattern('https?://[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,}.*')]],
      aiKeyword: ['', Validators.required],
    });
  }

  addAiOverviewRow(): void {
    const rows = this.aiOverviewRows;
    if (rows && rows.length < 10) {
      rows.push(this.createAiOverviewRowFormGroup());
    }
  }

  removeAiOverviewRow(index: number): void {
    if (this.aiOverviewRows.length > 1) {
      this.aiOverviewRows.removeAt(index);
    }
  }
  createCountryRowFormGroup(): FormGroup {
    return this.fb.group({
      country: ['', Validators.required],
      countryImpressions: [0, [Validators.required, Validators.min(0)]],
      countryClicks: [0, [Validators.required, Validators.min(0)]],
    });
  }
  addCountryRow(): void {
    const rows = this.countryRows;
    if (rows && rows.length < 10) {
      rows.push(this.createCountryRowFormGroup());
    }
  }
  removeCountryRow(index: number): void {
    if (this.countryRows.length > 1) {
      this.countryRows.removeAt(index);
    }
  }

  constructor() {
    effect(() => {
      this.isDarkTheme.set(this.themeService.theme() === 'dark');
    }
      // remove this because it is not needed 'allowSignalWrites' flag is deprecated and no longer impacts effect() (writes are always allowed)
    );

    this.form = this.fb.group({
      dateReference: [null, [Validators.required, this.dateRangeValidator()]],
      // SEO-Metrics tab fields
      clicks: [null, [Validators.min(0)]],
      impressions: [null, [Validators.min(0)]],
      activeVisitors: [null, [Validators.min(0)]],
      uniqueVisitors: [null, [Validators.min(0)]],
      traffic: [null, [Validators.min(0)]],
      // Bulk Arrays
      landingRows: this.fb.array([]),
      cityRows: this.fb.array([]),
      ratingRows: this.fb.array([]),
      reviewRows: this.fb.array([]),
      aiOverviewRows: this.fb.array([]),
      countryRows: this.fb.array([]),
    });

    // Initialize with one row for each
    this.addLandingRow();
    this.addCityRow();
    this.addRatingRow();
    this.addReviewRow();
    this.addAiOverviewRow();
    this.addCountryRow();

    this.landingPageForm = this.fb.group({
      url: ['', [Validators.required, Validators.pattern('https?://[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,}.*')]],
      clicks: [0, [Validators.required, Validators.min(0), Validators.pattern('^[0-9]*$')]],
      month: [null, [Validators.required]],
      year: [null, [Validators.required]],
    });

    this.cityForm = this.fb.group({
      city: ['', [Validators.required]],
      newUsers: [0, [Validators.required, Validators.min(0)]],
      month: [null, [Validators.required]],
      year: [null, [Validators.required]],
    });

    this.reviewForm = this.fb.group({
      platform: ['', [Validators.required]],
      rating: [null, [Validators.required, Validators.min(0), Validators.max(5)]],
      reviewsCount: [0, [Validators.required, Validators.min(0)]],
      month: [null, [Validators.required]],
      year: [null, [Validators.required]],
    });

    effect(() => {
      const productId =
        this.apiService.getSelectedProductId() ||
        sessionStorage.getItem('selected_product_id');

      const view = this.viewSignal();

      if (productId) {
        // Fix: Update signal inside untracked or ensure it doesn't cause loops.
        // Ideally, just use the local 'productId' variable for calls.
        untracked(() => {
          if (this.productId() !== productId) {
            this.dataFetchGeneration += 1;
            this.productId.set(productId);
            this.loadKeywords();
            this.loadAiKeywords();
          }
        });

        // Always track these for global cards/analytics
        const dateRange = this.selectedDateRange();

        if (view === 'analyticsView') {
          // --- ANALYTICS VIEW DEPENDENCIES ---

          untracked(() => {
            // ONLY these APIs will call when in Analytics View
            this.loadOverallPerformance();
            this.loadTopRecords();
            this.loadClickPerformanceDetails();
            this.loadCountries(1);
          });

        } else {
          // --- TABLE VIEW DEPENDENCIES ---
          // Register dependencies specific to Table View
          // This ensures changing pages in Table View doesn't re-trigger Analytics APIs
          const tableDate = this.selectedTableDate();
          const tableYear = this.selectedTableYear();

          const mainPage = this.currentPage();
          const lpPage = this.landingPagesPage();
          const cityPage = this.citiesPage();
          const countriesPage = this.countriesPage();
          const aiOverviewPage = this.aiOverviewPage();
          const reviewPage = this.reviewsPage();
          const detailedReviewPage = this.detailedReviewsPage();

          untracked(() => {
            // Still call these for global cards even in table view
            this.loadOverallPerformance();
            this.loadTopRecords();

            this.loadSEOStats(mainPage);
            this.loadLandingPages(lpPage);
            this.loadCityUsers(cityPage);
            this.loadCountries(this.countriesPage());
            this.loadAiOverview(aiOverviewPage);
            this.loadRatings(reviewPage);
            this.loadDetailedReviews(detailedReviewPage);
          });
        }
      }
    });

    effect(() => {
      const fromParent = this.catalogPageId();
      if (fromParent != null) {
        untracked(() => this.effectiveCatalogPageId.set(fromParent));
        return;
      }
      const productId = this.apiService.selectedProductId();
      if (!productId) return;
      untracked(() => {
        this.dashboardLayoutService.getCatalogProducts().subscribe((catalog) => {
          const activeProductPid = resolveActiveCatalogProductId(this.apiService);
          const pageId = findCatalogPageId(catalog as any[], this.PAGE_KEY, activeProductPid);
          if (pageId != null) this.effectiveCatalogPageId.set(pageId);
        });
      });
    });

    effect(() => {
      if (this.mixedMode()) return;
      const pageId = this.effectiveCatalogPageId();
      if (pageId == null) return;
      untracked(() => {
        this.customDashboardPin.hydratePinnedWidgetKeys(pageId).subscribe((keys) => {
          const pinned = new Set<SeoStatsSectionId>();
          keys.forEach((k) => {
            if (Object.values(SeoStatsSectionId).includes(k as SeoStatsSectionId)) {
              pinned.add(k as SeoStatsSectionId);
            }
          });
          this.pinnedSections.set(pinned);
        });
      });
    });

    // Pre-resolve catalog `widgets.id` per section so each Pin-to-Custom
    // click can skip the catalog round-trip AND so the template can hide
    // the pin button for sections with no backend counterpart (today:
    // `seo_audit_log` — frontend-only diagnostic block, no widget row).
    // Mirrors the Social Platform / Monthly Report 1 / 2 flow exactly:
    // one click sends exactly ONE pre-resolved widget id to the API,
    // never the whole page.
    effect(() => {
      if (this.mixedMode()) return;
      const pageId = this.effectiveCatalogPageId();
      if (pageId == null) return;
      const productId = resolveActiveCatalogProductId(this.apiService);
      untracked(() => {
        this.customDashboardPin
          .resolveCatalogWidgetIdsForPage(pageId, productId)
          .subscribe((widgetIdMap) => {
            const next = new Map<SeoStatsSectionId, number>();
            for (const sec of Object.values(SeoStatsSectionId)) {
              if (this.NEVER_PINNABLE_SECTIONS.has(sec)) continue;
              const id = widgetIdMap.get(sec);
              if (id != null) next.set(sec, id);
            }
            this.pinnableWidgetIds.set(next);
          });
      });
    });
  }

  /**
   * Checks if the metrics section has any data entered.
   */
  hasMetricsData(): boolean {
    const metrics = [
      'clicks',
      'impressions',
      'activeVisitors',
      'uniqueVisitors',
      'traffic',
    ];
    return metrics.some((key) => {
      const val = this.form.get(key)?.value;
      return (
        val !== null &&
        val !== undefined &&
        val !== '' &&
        (typeof val === 'string' ? val.trim().length > 0 : Number(val) > 0)
      );
    });
  }

  /**
   * Checks if a FormArray has any row with data.
   */
  hasArrayData(formArray: FormArray, fields: string[]): boolean {
    return formArray.controls.some((group) => {
      const g = group as FormGroup;
      return fields.some((key) => {
        const val = g.get(key)?.value;
        return (
          val !== null &&
          val !== undefined &&
          val !== '' &&
          (typeof val === 'string' ? val.trim().length > 0 : Number(val) > 0)
        );
      });
    });
  }

  isAtLeastOneMetricFilled(): boolean {
    if (this.isEditingMetric()) {
      return true;
    }

    if (this.hasMetricsData()) return true;
    if (this.hasArrayData(this.landingRows, ['landingUrl', 'landingClicks']))
      return true;
    if (this.hasArrayData(this.cityRows, ['cityName', 'cityUserCount']))
      return true;
    if (this.hasArrayData(this.countryRows, ['country', 'countryImpressions', 'countryClicks']))
      return true;
    if (
      this.hasArrayData(this.ratingRows, [
        'ratingPlatform',
        'ratingValue',
        'ratingCount',
      ])
    )
      return true;
    if (
      this.hasArrayData(this.reviewRows, [
        'reviewPlatform',
        'reviewText',
        'reviewerName',
      ])
    )
      return true;

    if (
      this.hasArrayData(this.aiOverviewRows, [
        'snapshotLink',
        'aiKeyword',
      ])
    )
      return true;

    return false;
  }

  ngOnInit(): void {
    // Hydrate layout (visibility / colSpan / order) and pinned-section state
    // from localStorage. This replaces the old `sectionLayout.init` call —
    // we now manage layout locally so the page can also support per-card
    // resize + drag-drop reordering.
    this._initLayout();
    this.themeService.theme$.subscribe((theme) => {
      this.isDarkTheme.set(theme === 'dark');
    });
    this.commonService.activeChatContextKey.set('SEO_STATS');
    this.setAiSuggestions();

    const productId = this.productId() || this.apiService.getSelectedProductId() || sessionStorage.getItem('selected_product_id');
    if (productId && this.commonService.isPanelOpen()) {
      const productName = this.productConfigService.getProductName(productId).toLowerCase();
      if (productName) {
        this.resolveAiAgent(productName);
      }
    }

    // Hydrate layout (visibility / colSpan / order) and pinned-section state
    // from localStorage. This replaces the old `sectionLayout.init` call —
    // we now manage layout locally so the page can also support per-card
    // resize + drag-drop reordering.
    this._initLayout();
    this.themeService.theme$.subscribe((theme) => {
      this.isDarkTheme.set(theme === 'dark');
    });
    this.loadProducts();
    // this.loadRatingPlatforms();
  }

  ngOnDestroy(): void {
    this.resolveAgentSubscription?.unsubscribe();
    this.commonService.activeAgentId.set(null);
    this.commonService.isPanelOpen.set(false);
    this.commonService.activeChatContextKey.set(null);
  }

  openAiAssistant(): void {
    const productId = this.productId() || this.apiService.getSelectedProductId() || sessionStorage.getItem('selected_product_id');
    if (!productId) return;

    const productName = this.productConfigService.getProductName(productId).toLowerCase();
    this.commonService.chatType.set('SEO_STATS');
    this.commonService.isPanelOpen.set(true);

    const contextKey = this.commonService.activeChatContextKey();
    if (contextKey) {
      const savedState = this.commonService.getChatState(contextKey);
      if (!savedState) {
        this.setAiSuggestions();
      }
    }

    this.resolveAiAgent(productName);
  }

  private setAiSuggestions(): void {
    this.commonService.aiSuggestions.set([
      'Show clicks details for the last 30 days.',
      'What was the traffic performance during the last 12 months?',
      'Show top keyword rankings from Jan 2025 to Dec 2025.',
      'Give me clicks over time for the last 90 days.',
    ]);
  }

  private resolveAiAgent(productName: string): void {
    this.resolveAgentSubscription?.unsubscribe();

    this.resolveAgentSubscription = this.aiBotService
      .resolveAgent(productName, 'seo_stats', 'ask_prism_ai')
      .subscribe({
        next: (res: any) => {
          const agentId = res?.agent_id || res?.data?.agent_id;
          this.commonService.activeAgentId.set(agentId ?? null);
        },
        error: () => {
          this.commonService.activeAgentId.set(null);
        },
      });
  }

  // ─── Section layout state (visibility + colSpan + order) ──────────────
  /** Persisted layout map driving the actual rendered page. */
  readonly layoutMap = signal<Record<SeoStatsSectionId, SectionLayoutItem>>(
    {} as Record<SeoStatsSectionId, SectionLayoutItem>,
  );

  /** All sections sorted by their persisted order. */
  readonly orderedSections = computed(() =>
    Object.values(this.layoutMap()).slice().sort((a, b) => a.order - b.order),
  );

  /** Sections currently visible, in order — used by the analytics-view grid. */
  readonly orderedVisibleSections = computed(() =>
    this.orderedSections().filter((s) => s.visible),
  );

  readonly embeddedVisibleSections = createEmbeddedSectionFilter(
    this.orderedVisibleSections,
    this.mixedMode,
    this.visibleWidgetKeys,
    this.orderedSections,
  );

  /**
   * Section ids that the host of this report is currently allowed to
   * render. When the component is the standalone `/seo-stats` page
   * (`mixedMode() === false`) every section is considered active so all
   * loaders run as before. When mounted inside `WidgetHostComponent`
   * (My Dashboard), only sections matching `visibleWidgetKeys` are
   * active — that's the set we gate data loaders on below to stop
   * firing API calls for cards the host never renders.
   *
   * IMPORTANT: derived directly from `visibleWidgetKeys()` (synchronous
   * host input), NOT from `embeddedVisibleSections()` — the latter
   * depends on `orderedVisibleSections` (loaded from localStorage in
   * `_initLayout`) which may not yet be populated at the moment the
   * productId effect fires the loaders.
   */
  private readonly activeSectionIds = computed<ReadonlySet<string>>(() => {
    const keys = this.visibleWidgetKeys();
    if (!keys || keys.length === 0) return new Set();
    const allowed = new Set<string>();
    for (const k of keys) {
      if (!k) continue;
      allowed.add(k);
      const stripped = stripProductPrefix(k);
      if (stripped !== k) allowed.add(stripped);
    }
    return allowed;
  });

  /**
   * Returns true if the given section is currently rendered by the host
   * (always true on the standalone page). Used as a one-line guard at
   * the top of each loader so embedded "single card" mounts skip the
   * data requests they don't need.
   */
  private isSectionActive(...sectionIds: SeoStatsSectionId[]): boolean {
    if (!this.mixedMode()) return true;
    const active = this.activeSectionIds();
    // Defensive: when host did not send any keys (or none resolved), fall
    // back to "active" so we never accidentally suppress a legitimate
    // request.
    if (active.size === 0) return true;
    return sectionIds.some((id) => active.has(id));
  }

  isVisible(sectionId: SeoStatsSectionId): boolean {
    return this.layoutMap()[sectionId]?.visible ?? true;
  }

  /** Tailwind col-span class for a section in the live 12-col grid. */
  getSectionColClass(item: SectionLayoutItem | SeoStatsSectionId): string {
    if (this.commonService.isPanelOpen()) return 'col-span-12';

    const id = typeof item === 'string' ? item : item.id;
    const span = this._normalizeColSpan(this.layoutMap()[id]?.colSpan ?? 12);
    switch (span) {
      case 4: return 'col-span-12 lg:col-span-4';
      case 6: return 'col-span-12 lg:col-span-6';
      case 8: return 'col-span-12 lg:col-span-8';
      case 12: default: return 'col-span-12';
    }
  }

  trackSection = (_idx: number, sec: SectionLayoutItem): string => sec.id;

  // ─── "Pin to Custom Dashboard" (API-backed, mirrors Social Platform) ────
  readonly pinnedSections = signal<Set<SeoStatsSectionId>>(new Set());
  readonly pendingPinSections = signal<Set<SeoStatsSectionId>>(new Set());

  /**
   * Sections that are explicitly excluded from the Custom ("My") Dashboard
   * by frontend rule, regardless of whether the backend catalog ever
   * advertises them. `seo_audit_log` is a page-local diagnostic surface —
   * it doesn't belong on the user's personalized dashboard, so we never
   * offer the pin button for it (and never seed it into the mixed
   * dashboard). Defensive denylist that runs IN ADDITION TO the catalog
   * gate.
   */
  private readonly NEVER_PINNABLE_SECTIONS: ReadonlySet<SeoStatsSectionId> =
    new Set<SeoStatsSectionId>([SeoStatsSectionId.AUDIT_LOG]);

  /**
   * Catalog `widgets.id` resolved per section, keyed by canonical
   * `SeoStatsSectionId`. Populated once in the constructor effect above
   * after the catalog products + page id resolve.
   *
   * Sections NOT present in this map have no backend catalog entry —
   * the template uses {@link canPinToCustomDashboard} to hide the pin
   * button for those (avoids the "Widget X is not registered" toast).
   * Sections that ARE in the map use the pre-resolved numeric `widgetId`
   * with {@link CustomDashboardPinService.toggleLayoutItem}, mirroring
   * the Social Platform / Monthly Report 1 / 2 pattern (one click =
   * one widget on the API, never the whole page).
   */
  readonly pinnableWidgetIds = signal<Map<SeoStatsSectionId, number>>(
    new Map(),
  );

  /**
   * Resolves the section id from either a layout item (template loops
   * iterate `SectionLayoutItem` objects via `embeddedVisibleSections()`)
   * or a raw enum value (TS callers).
   */
  private _toSectionId(
    sec: { id: SeoStatsSectionId } | SeoStatsSectionId,
  ): SeoStatsSectionId {
    return typeof sec === 'object' && sec !== null ? sec.id : sec;
  }

  isAddedToCustomDashboard(
    sec: { id: SeoStatsSectionId } | SeoStatsSectionId,
  ): boolean {
    return this.pinnedSections().has(this._toSectionId(sec));
  }

  isAddingToCustomDashboard(
    sec: { id: SeoStatsSectionId } | SeoStatsSectionId,
  ): boolean {
    return this.pendingPinSections().has(this._toSectionId(sec));
  }

  /**
   * Template gate for the pin button: returns `true` only when the
   * section is allowed on the Custom Dashboard AND has a backend
   * widget id resolved. Sections in {@link NEVER_PINNABLE_SECTIONS}
   * (Audit Log) and sections still loading return `false` so the
   * template hides the pin button instead of letting the user click
   * a known-failing action.
   */
  canPinToCustomDashboard(
    sec: { id: SeoStatsSectionId } | SeoStatsSectionId,
  ): boolean {
    const sectionId = this._toSectionId(sec);
    if (this.NEVER_PINNABLE_SECTIONS.has(sectionId)) return false;
    return this.pinnableWidgetIds().has(sectionId);
  }

  /**
   * Quick-action toggle handler. Mirrors `SocialPlatformComponent.addToCustomDashboard`:
   *   • Pre-resolved numeric `widgetId` (from {@link pinnableWidgetIds})
   *     is forwarded into {@link CustomDashboardPinService.toggleLayoutItem}.
   *   • The service performs the existence probe + `PATCH /visibility` or
   *     seed `POST /dashboards`. Only ONE widget id is sent — the
   *     dashboard never receives the whole page on a single click.
   */
  addToCustomDashboard(
    sec: { id: SeoStatsSectionId } | SeoStatsSectionId,
  ): void {
    const sectionId = this._toSectionId(sec);
    if (!sectionId) return;
    // Defense in depth — Audit Log (and any other denylisted section)
    // is never allowed on the Custom Dashboard, even if a future
    // template change exposes the button.
    if (this.NEVER_PINNABLE_SECTIONS.has(sectionId)) return;
    if (this.pendingPinSections().has(sectionId)) return;

    const pageId = this.effectiveCatalogPageId();
    if (pageId == null) return;

    const widgetId = this.pinnableWidgetIds().get(sectionId);
    if (widgetId == null) {
      // Frontend-only section (no backend widget). The template's
      // `canPinToCustomDashboard` gate hides the button for these, so
      // landing here means the catalog hasn't loaded yet.
      return;
    }

    const colSpan = this.layoutMap()[sectionId]?.colSpan ?? 12;

    this.customDashboardPin.toggleLayoutItem({
      pageId,
      widgetId,
      sourcePageId: pageId,
      colSpan,
      rowSpan: 1,
      customConfig: {},
      isCurrentlyPinned: this.isAddedToCustomDashboard(sectionId),
      onPinnedChange: (pinned) => {
        this.pinnedSections.update((s) => {
          const next = new Set(s);
          if (pinned) next.add(sectionId);
          else next.delete(sectionId);
          return next;
        });
      },
      onPendingChange: (pending) => {
        this.pendingPinSections.update((s) => {
          const next = new Set(s);
          if (pending) next.add(sectionId);
          else next.delete(sectionId);
          return next;
        });
      },
    });
  }

  // ─── Customize Layout editor (mini-dashboard preview) ──────────────────
  /** State for the inline Customize Layout editor. */
  readonly isLayoutEditorOpen = signal(false);
  readonly editorItems = signal<LayoutEditorItem[]>([]);

  /** Number of cards currently marked visible in the editor draft. */
  readonly editorVisibleCount = computed(
    () => this.editorItems().filter((i) => i.visible).length,
  );

  trackEditorItem = (_idx: number, item: LayoutEditorItem): string => item.id;

  /** Allowed col-span steps the user can cycle through per card. */
  private readonly COL_SPAN_STEPS: ReadonlyArray<number> = [4, 6, 8, 12];

  /** localStorage key for this page's persisted layout. */
  private readonly LAYOUT_LS_KEY = 'prism_seo_stats_layout_v1';

  /** Default col-span per section — chosen to match the historical visual
   *  layout where the 4 mid-page tables sit in a 2-col grid (6/12 each)
   *  and everything else is full width.
   *
   *  Only includes sections that participate in the customizable grid; AUDIT_LOG
   *  is rendered as a standalone block and is intentionally excluded. */
  private readonly DEFAULT_COL_SPAN: Partial<Record<SeoStatsSectionId, number>> = {
    [SeoStatsSectionId.STATS_OVERVIEW]: 12,
    [SeoStatsSectionId.CLICKS_CHART]: 12,
    [SeoStatsSectionId.KEYWORD_RANKING]: 6,
    [SeoStatsSectionId.AI_OVERVIEW]: 6,
    [SeoStatsSectionId.COUNTRY_PERFORMANCE]: 6,
    [SeoStatsSectionId.TOP_CITIES]: 6,
    [SeoStatsSectionId.REVIEWS_RATINGS]: 12,
    [SeoStatsSectionId.LATEST_REVIEWS]: 12,
  };

  openCustomizeLayout(): void {
    const titleById = new Map(this.SECTIONS.map((s) => [s.id, s.title]));
    const items: LayoutEditorItem[] = this.orderedSections()
      .filter((s) => !this.LAYOUT_EXCLUDED_SECTION_IDS.has(s.id))
      .map((s) => ({
        id: s.id,
        title: titleById.get(s.id) ?? s.id,
        visible: s.visible,
        colSpan: this._normalizeColSpan(s.colSpan),
        order: s.order,
      }));
    this.editorItems.set(items);
    this.isLayoutEditorOpen.set(true);
  }

  closeLayoutEditor(): void {
    this.isLayoutEditorOpen.set(false);
  }

  toggleEditorCardVisibility(id: SeoStatsSectionId): void {
    // When a card is hidden, push it to the bottom so the visible cards
    // reflow into the freed grid slot. When shown again, slot it back in
    // just above the first hidden item so it rejoins the visible group.
    this.editorItems.update((items) =>
      reorderLayoutEditorItemsOnVisibilityToggle(items, id),
    );
  }

  /** Restores the editor draft to the page's default order, widths, and visibility. */
  resetLayoutEditorToDefaults(): void {
    const defaults: LayoutEditorItem[] = this.LAYOUT_SECTIONS.map((s, idx) => ({
      id: s.id as SeoStatsSectionId,
      title: s.title,
      visible: true,
      colSpan: this._normalizeColSpan(
        this.DEFAULT_COL_SPAN[s.id as SeoStatsSectionId] ?? 12,
      ),
      order: idx,
    }));
    this.editorItems.set(defaults);
  }

  /** Persists the editor draft to local layout state + localStorage. */
  applyLayoutEditor(): void {
    const draft = this.editorItems();
    const layout: Record<SeoStatsSectionId, SectionLayoutItem> =
      {} as Record<SeoStatsSectionId, SectionLayoutItem>;
    draft.forEach((d, idx) => {
      layout[d.id] = {
        id: d.id,
        visible: d.visible,
        colSpan: this._normalizeColSpan(d.colSpan),
        order: idx,
      };
    });
    this.layoutMap.set(layout);
    this._saveLayoutToStorage(layout);

    // Best-effort backend persistence via the shared
    // `CustomizeLayoutBatchService` — same flow as Social Platform.
    // Service no-ops silently when `pageId` / catalog isn't ready or
    // the user has no mixed dashboard yet, so existing local-only
    // behaviour is preserved.
    const pageId = this.effectiveCatalogPageId();
    if (pageId != null) {
      this._customizeLayoutBatch.applyEditorDraftToPageDashboardByKeys({
        pageId,
        productId: resolveActiveCatalogProductId(this.apiService),
        fallbackSourcePageId: pageId,
        draft: draft.map((d, idx) => ({
          key: String(d.id),
          sourcePageId: pageId,
          colSpan: this._normalizeColSpan(d.colSpan),
          visible: d.visible,
          customConfig: null,
          order: idx,
        })),
      });
    }

    this.isLayoutEditorOpen.set(false);
  }

  // ─── Editor: width chips (decrease / increase / labels) ─────────────────
  /** Steps a card down one width. Respects each card's designed default as the lower bound. */
  decreaseEditorCardColSpan(id: SeoStatsSectionId): void {
    this.editorItems.update((items) =>
      items.map((i) => {
        if (i.id !== id) return i;
        const idx = this.COL_SPAN_STEPS.indexOf(this._normalizeColSpan(i.colSpan));
        if (idx <= 0) return i;
        const nextSpan = this.COL_SPAN_STEPS[idx - 1];
        if (this._isBelowDefaultColSpan(i.id, nextSpan)) return i;
        return { ...i, colSpan: nextSpan };
      }),
    );
  }

  increaseEditorCardColSpan(id: SeoStatsSectionId): void {
    this.editorItems.update((items) =>
      items.map((i) => {
        if (i.id !== id) return i;
        const idx = this.COL_SPAN_STEPS.indexOf(this._normalizeColSpan(i.colSpan));
        if (idx < 0 || idx >= this.COL_SPAN_STEPS.length - 1) return i;
        return { ...i, colSpan: this.COL_SPAN_STEPS[idx + 1] };
      }),
    );
  }

  /**
   * Auto-adjusts widths in the editor so every row fully spans the 12-col
   * grid. Delegates to the shared {@link computeLayoutEditorAutoWidthUpdates}
   * helper, which grows the smallest card in each leaky row (and shares the
   * leftover equally when multiple cards tie at the smallest width).
   */
  autoAdjustEditorWidths(): void {
    const updates = computeLayoutEditorAutoWidthUpdates(this.editorItems());
    if (!updates.size) return;
    this.editorItems.update((items) =>
      applyLayoutEditorAutoWidthUpdates(items, updates),
    );
  }

  /** Whether clicking Auto Adjust Width would actually grow at least one card. */
  canAutoAdjustEditorWidths(): boolean {
    return computeLayoutEditorAutoWidthUpdates(this.editorItems()).size > 0;
  }

  /**
   * Whether the minimize button should be active for this card. A card
   * cannot shrink below its designed default col-span — e.g. a card
   * designed for `col-8` can only step between 8 and 12.
   */
  canDecreaseEditorCardColSpan(item: LayoutEditorItem): boolean {
    const idx = this.COL_SPAN_STEPS.indexOf(this._normalizeColSpan(item.colSpan));
    if (idx <= 0) return false;
    if (this._isBelowDefaultColSpan(item.id, this.COL_SPAN_STEPS[idx - 1])) {
      return false;
    }
    return true;
  }

  /** Whether `span` is below the designed default col-span for `id`. */
  private _isBelowDefaultColSpan(id: SeoStatsSectionId, span: number): boolean {
    const defaultSpan = this.DEFAULT_COL_SPAN[id];
    if (defaultSpan == null) return false;
    return span < this._normalizeColSpan(defaultSpan);
  }

  canIncreaseEditorCardColSpan(item: LayoutEditorItem): boolean {
    const idx = this.COL_SPAN_STEPS.indexOf(this._normalizeColSpan(item.colSpan));
    return idx >= 0 && idx < this.COL_SPAN_STEPS.length - 1;
  }

  /** Tailwind col-span class for an editor tile inside the popup's 12-col grid. */
  getEditorColSpanClass(span: number): string {
    switch (this._normalizeColSpan(span)) {
      case 4: return 'col-span-4';
      case 6: return 'col-span-6';
      case 8: return 'col-span-8';
      case 12: return 'col-span-12';
      default: return 'col-span-12';
    }
  }

  /** Human-friendly label for a col-span value (Small / Medium / Large / Full). */
  getEditorColSpanLabel(span: number): string {
    switch (this._normalizeColSpan(span)) {
      case 4: return 'Small';
      case 6: return 'Medium';
      case 8: return 'Large';
      case 12: return 'Full';
      default: return `${span}`;
    }
  }

  /* Swaps the dragged card with the card at the drop target so that ONLY those two tiles exchange positions in the array; every other card keeps its current array slot (no shuffle/cascade like CDK's default `moveItemInArray`). we changed the strategy from shift to pairwise-swap [event, { strategy: 'shift' });]*/
  onEditorCardDrop(event: CdkDragDrop<LayoutEditorItem[]>): void {
    const next = applyLayoutEditorCardDrop(this.editorItems(), event, {
      strategy: 'pairwise-swap',
    });
    if (next) this.editorItems.set(next);
  }
  // ─── Layout state private helpers ──────────────────────────────────────
  private _initLayout(): void {
    const stored = this._loadLayoutFromStorage();
    const layout: Record<SeoStatsSectionId, SectionLayoutItem> =
      {} as Record<SeoStatsSectionId, SectionLayoutItem>;

    this.LAYOUT_SECTIONS.forEach((s, idx) => {
      const sid = s.id as SeoStatsSectionId;
      const storedItem = stored?.[sid];
      layout[sid] = {
        id: sid,
        visible: storedItem?.visible ?? true,
        colSpan: this._normalizeColSpan(
          storedItem?.colSpan ?? this.DEFAULT_COL_SPAN[sid] ?? 12,
        ),
        order: storedItem?.order ?? idx,
      };
    });

    this.layoutMap.set(layout);
  }

  private _loadLayoutFromStorage(): Record<string, SectionLayoutItem> | null {
    try {
      const raw = localStorage.getItem(this.LAYOUT_LS_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  }

  private _saveLayoutToStorage(
    layout: Record<SeoStatsSectionId, SectionLayoutItem>,
  ): void {
    try {
      localStorage.setItem(this.LAYOUT_LS_KEY, JSON.stringify(layout));
    } catch {
      // localStorage unavailable (e.g. private mode) — silently ignore.
    }
  }

  private _normalizeColSpan(span: number | null | undefined): number {
    const allowed = [4, 6, 8, 12];
    if (span == null || !Number.isFinite(span)) return 12;
    if (allowed.includes(span)) return span;
    if (span <= 4) return 4;
    if (span <= 6) return 6;
    if (span <= 8) return 8;
    return 12;
  }

  onKeywordInput(value: string, index: number, tab: 'landing' | 'ai-overview'): void {
    this.activeKeywordSearch.set(value || '');
    this.activeRowIndex.set(index);
    this.activeKeywordTab.set(tab);
    this.showAutocompleteManual.set(!!(value && value.trim()));
  }

  selectKeyword(keyword: string, index: number, tab: 'landing' | 'ai-overview'): void {
    const rows = tab === 'landing' ? this.landingRows : this.aiOverviewRows;
    const fieldName = tab === 'landing' ? 'landingUrl' : 'aiKeyword';

    const control = rows.at(index).get(fieldName);
    if (control) {
      // Use emitEvent: false to prevent triggering onKeywordInput again if it's bound to valueChange
      control.setValue(keyword, { emitEvent: false });
      control.markAsDirty();
    }
    this.activeKeywordSearch.set(keyword);
    this.showAutocompleteManual.set(false);
  }

  closeAutocomplete(): void {
    setTimeout(() => {
      this.showAutocompleteManual.set(false);
    }, 150);
  }

  @HostListener('document:mousedown', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    if (!target) return;

    const isInsideAutocomplete = target.closest('.autocomplete-dropdown');
    const isInsideInput = target.closest('.keyword-input-container');

    if (!isInsideAutocomplete && !isInsideInput) {
      this.showAutocompleteManual.set(false);
      this.activeRowIndex.set(null);
    }
  }

  // ... (Rest of your existing methods: constructor, ngOnInit, openPanel, submitForm, etc.)
  // Ensure openPanel calls loadKeywords()

  setActiveTab(tab: any): void {
    this.viewSignal.set(tab);
  }

  private getActiveProductId(): string | null {
    return (
      this.productId() ||
      this.apiService.getSelectedProductId() ||
      sessionStorage.getItem('selected_product_id')
    );
  }

  private captureProductFetchContext(): { generation: number; productId: string } | null {
    const productId = this.getActiveProductId();
    if (!productId) return null;
    return { generation: this.dataFetchGeneration, productId };
  }

  private isStaleProductFetch(ctx: { generation: number; productId: string }): boolean {
    const currentProductId = this.getActiveProductId();
    return ctx.generation !== this.dataFetchGeneration || ctx.productId !== currentProductId;
  }

  loadTopRecords(): void {
    if (
      !this.isSectionActive(
        SeoStatsSectionId.KEYWORD_RANKING,
        SeoStatsSectionId.AI_OVERVIEW,
        SeoStatsSectionId.TOP_CITIES,
        SeoStatsSectionId.REVIEWS_RATINGS,
        SeoStatsSectionId.LATEST_REVIEWS,
        SeoStatsSectionId.COUNTRY_PERFORMANCE,
      )
    ) {
      return;
    }

    const productId = this.productId() || this.apiService.getSelectedProductId();

    const fetchCtx = this.captureProductFetchContext();
    if (!fetchCtx) return;

    const params: any = { productId: fetchCtx.productId };

    // No range picked → the request layer defaults the SEO window to the last 12 months.
    if (this.selectedDateRange()) {
      params.startDate = this.selectedDateRange()?.start;
      params.endDate = this.selectedDateRange()?.end;
    }

    this.apiService.get('/seo/top-records', params).subscribe({
      next: (response: any) => {
        if (this.isStaleProductFetch(fetchCtx)) return;
        const data = response.data || response;

        if (data.topLandingPages) {
          const mappedLandingPages = (
            Array.isArray(data.topLandingPages) ? data.topLandingPages : []
          ).map((item: any, index: number) => ({
            id: item.id,
            url: item.url,
            clicks: item.clicks,
            date: item.date
          }));

          this.landingPages.set(mappedLandingPages);
        }

        if (data.topCityUsers) {
          const mappedCities = (
            Array.isArray(data.topCityUsers) ? data.topCityUsers : []
          ).map((item: any, index: number) => ({
            id: item.id,
            city: item.city,
            userCount: item.userCount,
            date: item.date
          }));

          this.topCities.set(mappedCities);
        }

        if (data.topRatings) {
          const mappedData = (
            Array.isArray(data.topRatings) ? data.topRatings : []
          ).map((item: any) => ({
            ...item,
            platformId:
              item.platform && item.platform.id
                ? item.platform.id
                : (item.platformId || null),
            platform:
              item.platform && item.platform.name
                ? item.platform.name
                : item.platform,
            reviewsCount:
              item.count !== undefined ? item.count : item.reviewsCount,
            displayDate: this.formatDateToMMYYYY(item.date),
            icon:
              item.icon ||
              (item.platform &&
                item.platform.name &&
                item.platform.name.toLowerCase().includes('google')
                ? 'google'
                : 'star'),
            latestReviews: item.latestReviews || [],
          }));

          this.topReviewsRatings.set(mappedData);
          const hasSelected = mappedData.some((p: any) => p.platformId === this.selectedPlatformId());
          if (mappedData.length > 0 && (!this.selectedPlatformId() || !hasSelected)) {
            this.selectedPlatformId.set(mappedData[0].platformId);
          }
        }

        if (data.topAiOverviewKeywords) {
          const mappedAiOverview = (
            Array.isArray(data.topAiOverviewKeywords) ? data.topAiOverviewKeywords : []
          ).map((item: any) => ({
            id: item.id,
            keyword: item.keyword,
            snapshotUrl: item.snapshotUrl,
            date: item.date
          }));
          this.topAiOverviewKeywords.set(mappedAiOverview);
        }

        if (data.topKeywordRanking) {
          const mappedKeywordRanking = (
            Array.isArray(data.topKeywordRanking) ? data.topKeywordRanking : []
          ).map((item: any) => ({
            id: item.id,
            keyword: item.keyword,
            rank: item.rank,
            date: item.date
          }));
          this.topKeywordRanking.set(mappedKeywordRanking);
        }

        if (data.topCountryStats) {
          const mappedCountryStats = (
            Array.isArray(data.topCountryStats) ? data.topCountryStats : []
          ).map((item: any) => ({
            id: item.id,
            country: item.country?.name || item.country || 'Unknown',
            impressions: item.impressions ?? 0,
            clicks: item.clicks ?? 0,
            date: item.date,
          }));
          this.topCountryStats.set(mappedCountryStats);
        }
      },
      error: (error) => {
        console.error('Error loading top records:', error);
      },
    });
  }

  parsePeriod(period: string) {
    // Month: "2025-02"
    if (!period.includes(':')) {
      return {
        key: period,
        start: `${period}-01`,
      };
    }

    const [start, end] = period.split(':');

    // Year: "2025-01:2025-12"
    if (start.endsWith('-01') && end.endsWith('-12')) {
      return {
        key: start.slice(0, 4), // "2025"
        start: `${start.slice(0, 4)}-01-01`,
      };
    }

    // Quarter: "2025-01:2025-03"
    const startMonth = Number(start.split('-')[1]);
    const quarter = Math.floor((startMonth - 1) / 3) + 1;

    return {
      key: `Q${quarter} ${start.slice(0, 4)}`, // "Q1 2025"
      start: `${start}-01`,
    };
  }

  loadClickPerformanceDetails(): void {
    if (!this.isSectionActive(SeoStatsSectionId.CLICKS_CHART)) return;

    const productId =
      this.productId() || this.apiService.getSelectedProductId();

    const fetchCtx = this.captureProductFetchContext();
    if (!fetchCtx) return;

    const params: any = {
      productId: fetchCtx.productId,
      unit: this.unit() || 'month',
    };

    // No range picked → the request layer defaults the SEO window to the last 12 months.
    if (this.selectedDateRange()) {
      params.startDate = this.selectedDateRange()?.start;
      params.endDate = this.selectedDateRange()?.end;
    }

    this.apiService.get('/seo/click-performance-details', params).subscribe({
      next: (response: any) => {
        if (this.isStaleProductFetch(fetchCtx)) return;
        const data = response.data || response;
        if (Array.isArray(data)) {
          const mappedData = data.map((item: any) => {
            const parsed = this.parsePeriod(item.period);
            return {
              period: item.period,
              date: item.period,
              value: Number(item.click || 0), // API returns 'click', not 'clicks'
              start: parsed.start,
              end: parsed.start,
              label: item.period,
              key: 'Clicks',
            };
          });

          this.clicksChartData.set(mappedData);
        }
      },
      error: (error) => {
        console.error('Error loading click performance details:', error);
      },
    });
  }

  onDateRangeFilter(dateValue: any): void {
    const { start_date, end_date, unit } = dateValue;
    this.unit.set(unit || 'month');

    const parseDate = (dStr: string) => {
      if (!dStr) return dayjs();
      if (dStr.includes('T') || dStr.includes('Z')) {
        return dayjs.utc(dStr).tz(this.LA_TIMEZONE);
      }
      return dayjs(dStr);
    };

    const finalStartDate =
      start_date?.length === 7
        ? start_date
        : parseDate(start_date).format('YYYY-MM');
    const finalEndDate =
      end_date?.length === 7
        ? end_date
        : parseDate(end_date).format('YYYY-MM');

    const current = this.selectedDateRange();
    if (finalStartDate && finalEndDate) {
      // The constructor effect (around L1286) reads `selectedDateRange()`
      // as a tracked dependency and re-runs the analytics-view loaders
      // (including `loadClickPerformanceDetails()`) automatically when
      // the range changes. The previous code below ALSO called
      // `this.loadClickPerformanceDetails()` directly after `.set()`,
      // producing a duplicate `GET /v1/seo/click-performance-details`
      // every time the range changed — visible most clearly when the
      // report is embedded in My Dashboard, because the host pushes
      // the range here via `dispatchDateRangeToRef()`.
      this.selectedDateRange.set({
        start: finalStartDate,
        end: finalEndDate,
      });
    } else if (current !== null) {
      this.selectedDateRange.set(null);
    }
  }

  loadLandingPages(page: number = 1): void {
    if (!this.isSectionActive(SeoStatsSectionId.KEYWORD_RANKING)) return;

    // const fetchCtx = this.captureProductFetchContext();
    // if (!fetchCtx) return;
    const productId = this.apiService.getSelectedProductId();

    const params: any = {
      // productId: fetchCtx.productId,
      productId,
      page,
      limit: this.itemsPerPage,
    };

    if (this.selectedTableDate()?.start) {
      params.date = dayjs.utc(this.selectedTableDate()?.start).tz(this.LA_TIMEZONE).format('YYYY-MM');
    } else if (this.selectedTableYear()?.start) {
      params.date = this.selectedTableYear()?.start;
    } else if (this.selectedDateRange()?.start) {
      params.date = dayjs.utc(this.selectedDateRange()?.start).tz(this.LA_TIMEZONE).format('YYYY-MM');
    }


    this.apiService.get('/seo/keyword-rankings', params).subscribe({
      next: (response: any) => {
        // if (this.isStaleProductFetch(fetchCtx)) return;
        let data = response.data || response.list || response || [];
        const pagination = data.pagination || response.pagination;

        if (data && data.rankings) {
          data = data.rankings;
        }

        const mappedData = (Array.isArray(data) ? data : []).map(
          (item: any) => ({
            ...item,
            // Mapping for compatibility if needed, but 'keyword' and 'rank' should be in item
            keyword: item.keyword,
            rank: item.rank,
            period: item.date,
          })
        );
        this.landingPages.set(mappedData);

        if (pagination && pagination.total !== undefined) {
          this.totalLandingPages.set(pagination.total);
        } else {
          // Fallback if no pagination data
          this.totalLandingPages.set(data.length);
        }

        this.landingPagesPage.set(page);
      },
      error: (error) => {
        console.error('Error loading landing pages:', error);
      },
    });
  }

  onLandingPageChange(page: number): void {
    this.loadLandingPages(page);
  }

  loadCityUsers(page: number = 1): void {
    if (!this.isSectionActive(SeoStatsSectionId.TOP_CITIES)) return;

    // const fetchCtx = this.captureProductFetchContext();
    // if (!fetchCtx) return;
    const productId = this.apiService.getSelectedProductId();

    const params: any = {
      // productId: fetchCtx.productId,
      productId,
      page,
      limit: this.itemsPerPage,
    };

    if (this.selectedTableDate()?.start) {
      params.date = dayjs.utc(this.selectedTableDate()?.start).tz(this.LA_TIMEZONE).format('YYYY-MM');
    } else if (this.selectedTableYear()?.start) {
      params.date = this.selectedTableYear()?.start;
    } else if (this.selectedDateRange()?.start) {
      params.date = dayjs.utc(this.selectedDateRange()?.start).tz(this.LA_TIMEZONE).format('YYYY-MM');
    }

    this.apiService.get('/seo/city-users', params).subscribe({
      next: (response: any) => {
        // if (this.isStaleProductFetch(fetchCtx)) return;
        let data = response.data || response.list || response || [];
        const pagination = data.pagination || response.pagination;

        if (data && data.cityUsers) {
          data = data.cityUsers;
        }

        const mappedData = (Array.isArray(data) ? data : []).map(
          (item: any) => ({
            ...item,
            period: item.date,
          })
        );
        this.topCities.set(mappedData);

        if (pagination && pagination.total !== undefined) {
          this.totalCityUsers.set(pagination.total);
        } else {
          // Fallback if no pagination data
          this.totalCityUsers.set(data.length);
        }

        this.citiesPage.set(page);
      },
      error: (error) => {
        console.error('Error loading city users:', error);
      },
    });
  }

  loadRatings(page: number = 1): void {
    if (!this.isSectionActive(SeoStatsSectionId.REVIEWS_RATINGS)) return;

    // const fetchCtx = this.captureProductFetchContext();
    // if (!fetchCtx) return;
    const productId = this.apiService.getSelectedProductId();

    const params: any = {
      // productId: fetchCtx.productId,
      productId,
      page,
      limit: this.itemsPerPage,
    };

    if (this.selectedTableDate()?.start) {
      params.date = dayjs.utc(this.selectedTableDate()?.start).tz(this.LA_TIMEZONE).format('YYYY-MM');
    } else if (this.selectedTableYear()?.start) {
      params.date = this.selectedTableYear()?.start;
    } else if (this.selectedDateRange()?.start) {
      params.date = dayjs.utc(this.selectedDateRange()?.start).tz(this.LA_TIMEZONE).format('YYYY-MM');
    }

    this.apiService.get('/seo/ratings', params).subscribe({
      next: (response: any) => {
        // if (this.isStaleProductFetch(fetchCtx)) return;
        let data = response.data || response.list || response || [];
        const pagination = data.pagination || response.pagination;

        if (data && data.ratings) {
          data = data.ratings;
        }

        // Map data if needed to ensure optional fields like 'icon' exist
        const mappedData = (Array.isArray(data) ? data : []).map(
          (item: any) => ({
            ...item,
            platformId:
              item.platform && item.platform.id
                ? item.platform.id
                : (item.platformId || null),
            platform:
              item.platform && item.platform.name
                ? item.platform.name
                : item.platform,
            reviewsCount:
              item.count !== undefined ? item.count : item.reviewsCount,
            period: item.date,
            ratingGrowth: item.ratingGrowth,
            icon: item.icon || 'star', // Provide default icon if missing
          })
        );
        this.reviewsRatings.set(mappedData);

        if (pagination && pagination.total !== undefined) {
          this.totalReviews.set(pagination.total);
        } else {
          // Fallback if no pagination data
          this.totalReviews.set(data.length);
        }

        this.reviewsPage.set(page);
      },
      error: (error) => {
        console.error('Error loading ratings:', error);
      },
    });
  }

  loadDetailedReviews(page: number = 1): void {
    if (!this.isSectionActive(SeoStatsSectionId.LATEST_REVIEWS)) return;

    const productId = this.apiService.getSelectedProductId();
    const params: any = {
      // productId: fetchCtx.productId,
      productId,
      page,
      limit: this.itemsPerPage,
    };

    if (this.selectedTableDate()?.start) {
      params.date = dayjs(this.selectedTableDate()?.start).format('YYYY-MM');
    } else if (this.selectedTableYear()?.start) {
      params.date = this.selectedTableYear()?.start;
    } else if (this.selectedDateRange()?.start) {
      params.date = dayjs(this.selectedDateRange()?.start).format('YYYY-MM');
    }

    this.apiService.get('/seo/reviews', params).subscribe({
      next: (response: any) => {
        // if (this.isStaleProductFetch(fetchCtx)) return;
        let data = response.data || response.list || response || [];
        const pagination = data.pagination || response.pagination;

        if (data && data.reviews) {
          data = data.reviews;
        }

        this.detailedReviews.set(Array.isArray(data) ? data : []);

        if (pagination && pagination.total !== undefined) {
          this.totalDetailedReviews.set(pagination.total);
        } else {
          this.totalDetailedReviews.set(data.length);
        }

        this.detailedReviewsPage.set(page);
      },
      error: (error) => {
        console.error('Error loading detailed reviews:', error);
      },
    });
  }

  onDetailedReviewPageChange(page: number): void {
    this.loadDetailedReviews(page);
  }

  loadRatingPlatforms(): void {
    if (!this.isSectionActive(SeoStatsSectionId.REVIEWS_RATINGS)) return;

    const productId = this.apiService.getSelectedProductId();

    // const fetchCtx = this.captureProductFetchContext();
    // if (!fetchCtx) return;

    this.apiService.get('/seo/rating-platforms', { productId }).subscribe({
      next: (response: any) => {
        // if (this.isStaleProductFetch(fetchCtx)) return;
        let data = response.data || response.list || response || [];
        if (data && data.platforms) {
          data = data.platforms;
        }

        const options: DropdownOption[] = (Array.isArray(data) ? data : []).map(
          (item: any) => {
            // Handle both string and object responses
            const label =
              typeof item === 'string'
                ? item
                : item.name || item.label || 'Unknown';
            const value =
              typeof item === 'string'
                ? item
                : item.id || item.value || item.name;
            return { label, value };
          }
        );

        this.platformPids = new Map(
          (Array.isArray(data) ? data : [])
            .filter((item: any) => item?.id != null && item?.pid != null)
            .map((item: any) => [String(item.id), Number(item.pid)])
        );
        this.platformOptions.set(options);
      },
      error: (error) => {
        console.error('Error loading rating platforms:', error);
      },
    });
  }

  loadKeywords(): void {
    if (!this.isSectionActive(SeoStatsSectionId.KEYWORD_RANKING)) return;

    const productId = this.productId() || this.apiService.getSelectedProductId();
    if (!productId) return;

    // const fetchCtx = this.captureProductFetchContext();
    // if (!fetchCtx) return;

    this.apiService.get(`/seo/keywords/${productId}`).subscribe({
      next: (response: any) => {
        // if (this.isStaleProductFetch(fetchCtx)) return;
        // Handle various response formats
        const data = response.data || response;
        const keywords = Array.isArray(data.keywords) ? data.keywords :
          (Array.isArray(data) ? data : []);

        const options: DropdownOption[] = keywords.map((kw: any) => ({
          label: typeof kw === 'string' ? kw : (kw.keyword || kw.label || kw.name || 'Unknown'),
          value: typeof kw === 'string' ? kw : (kw.id || kw.keyword || kw.value || kw)
        }));

        this.keywordOptions.set(options);
      },
      error: (error) => console.error('Error loading keywords:', error),
    });
  }

  loadAiKeywords(): void {
    if (!this.isSectionActive(SeoStatsSectionId.AI_OVERVIEW)) return;

    const productId = this.productId() || this.apiService.getSelectedProductId();
    if (!productId) return;

    // const fetchCtx = this.captureProductFetchContext();
    // if (!fetchCtx) return;

    this.apiService.get(`/seo/ai-keywords/${productId}`).subscribe({
      next: (response: any) => {
        // if (this.isStaleProductFetch(fetchCtx)) return;
        const data = response.data || response;
        const keywords = Array.isArray(data.keywords) ? data.keywords :
          (Array.isArray(data) ? data : []);

        const options: DropdownOption[] = keywords.map((kw: any) => ({
          label: typeof kw === 'string' ? kw : (kw.keyword || kw.label || kw.name || 'Unknown'),
          value: typeof kw === 'string' ? kw : (kw.id || kw.keyword || kw.value || kw)
        }));

        this.aiKeywordOptions.set(options);
      },
      error: (error) => console.error('Error loading AI keywords:', error),
    });
  }

  loadCountryOptions(): void {
    if (!this.isSectionActive(SeoStatsSectionId.COUNTRY_PERFORMANCE)) return;

    this.apiService.get('/countries').subscribe({
      next: (response: any) => {
        const data = response.data || response.list || response || [];
        const options: DropdownOption[] = (Array.isArray(data) ? data : []).map(
          (item: any) => {
            const label = item.name || item.label || item.country_name || 'Unknown';
            const value = item.id || item.code || item.country_code || item;
            return { label, value };
          }
        );
        this.countryOptions.set(options);
      },
      error: (error) => {
        console.error('Error loading countries:', error);
      },
    });
  }

  loadSEOStats(page: number = 1): void {
    if (!this.isSectionActive(SeoStatsSectionId.STATS_OVERVIEW)) return;

    // const fetchCtx = this.captureProductFetchContext();
    // if (!fetchCtx) return;

    this.isLoading.set(true);
    this.errorMessage.set('');
    const productId = this.apiService.getSelectedProductId();

    const params: any = {
      productId: productId,
      limit: this.itemsPerPage,
      page: page,
    };

    if (this.selectedTableDate()?.start) {
      params.date = dayjs(this.selectedTableDate()?.start).format('YYYY-MM');
    } else if (this.selectedTableYear()?.start) {
      params.date = this.selectedTableYear()?.start;
    } else if (this.selectedDateRange()?.start) {
      params.date = dayjs(this.selectedDateRange()?.start).format('YYYY-MM');
    }

    this.apiService.get('/seo/metrics', params).subscribe({
      next: (response: any) => {
        // if (this.isStaleProductFetch(fetchCtx)) {
        //   this.isLoading.set(false);
        //   return;
        // }
        // Correctly handle the API response structure based on the provided JSON
        const data =
          response.seoMetrics ||
          response.metrics ||
          response.data ||
          response.list ||
          (Array.isArray(response) ? response : []);

        const mappedData = (Array.isArray(data) ? data : []).map(
          (item: any) => ({
            ...item,
            period: item.date,
          })
        );

        this.tableData.set(mappedData);

        // Update pagination if available in response
        if (response.pagination) {
          this.totalItems.set(response.pagination.total || data.length);
        } else if (response.total) {
          this.totalItems.set(response.total);
        } else {
          this.totalItems.set(data.length);
        }

        this.currentPage.set(page);
        this.isLoading.set(false);
      },
      error: (error) => {
        this.errorMessage.set('Failed to load SEO statistics');
        this.isLoading.set(false);
        console.error('Error loading SEO stats:', error);
      },
    });
  }

  loadOverallPerformance(): void {
    if (!this.isSectionActive(SeoStatsSectionId.STATS_OVERVIEW)) return;

    // const fetchCtx = this.captureProductFetchContext();
    // if (!fetchCtx) return;
    const productId = this.apiService.getSelectedProductId();

    const params: any = {};
    // No range picked → the request layer defaults the SEO window to the last 12 months.
    if (this.selectedDateRange()) {
      params.startDate = this.selectedDateRange()?.start;
      params.endDate = this.selectedDateRange()?.end;
    }

    this.apiService
      .get(`/seo/overall-seo-performance/${productId}`, params)
      .subscribe({
        next: (response: any) => {
          // if (this.isStaleProductFetch(fetchCtx)) return;
          if (response) {
            const stats = response.data || response;
            const parseVal = (val: any) =>
              val === 'NA' || val === null || val === undefined
                ? 0
                : typeof val === 'string'
                  ? parseFloat(val)
                  : val;

            this.seoStats.set({
              traffic: parseVal(stats.traffic),
              activeVisitors: parseVal(stats.activeVisitors),
              uniqueVisitors: parseVal(stats.uniqueVisitors),
              clicks: parseVal(stats.clicks),
              impressions: parseVal(stats.impressions),
              trafficChange: parseVal(stats.growth?.traffic),
              activeVisitorsChange: parseVal(stats.growth?.activeVisitors),
              uniqueVisitorsChange: parseVal(stats.growth?.uniqueVisitors),
              clicksChange: parseVal(stats.growth?.clicks),
              impressionsChange: parseVal(stats.growth?.impressions),
            });
          }
        },
        error: (error) => {
          console.error('Error loading Overall SEO Performance:', error);
        },
      });
  }

  onPageChange(page: number): void {
    this.loadSEOStats(page);
  }

  onCityPageChange(page: number): void {
    this.loadCityUsers(page);
  }

  loadCountries(page: number = 1): void {
    if (!this.isSectionActive(SeoStatsSectionId.COUNTRY_PERFORMANCE)) return;

    // const fetchCtx = this.captureProductFetchContext();
    // if (!fetchCtx) return;
    const productId = this.apiService.getSelectedProductId();

    const params: any = {
      // productId: fetchCtx.productId,
      productId: productId,
      page,
      limit: this.itemsPerPage,
    };

    if (this.selectedTableDate()?.start) {
      params.date = dayjs(this.selectedTableDate()?.start).format('YYYY-MM');
    } else if (this.selectedTableYear()?.start) {
      params.date = this.selectedTableYear()?.start;
    } else if (this.selectedDateRange()?.start) {
      params.date = dayjs(this.selectedDateRange()?.start).format('YYYY-MM');
    }

    this.apiService.get('/seo/country-stats', params).subscribe({
      next: (response: any) => {
        // if (this.isStaleProductFetch(fetchCtx)) return;
        let data = response.data || response.list || response || [];
        const pagination = data.pagination || response.pagination;

        if (data && data.countryStats) {
          data = data.countryStats;
        }

        const mappedData = (Array.isArray(data) ? data : []).map(
          (item: any) => ({
            ...item,
            countryId: item.country?.id || item.countryId,
            country: item.country?.name || item.country || 'Unknown',
            period: item.date,
          })
        );
        this.countries.set(mappedData);

        if (pagination && pagination.total !== undefined) {
          this.totalCountries.set(pagination.total);
        } else {
          this.totalCountries.set(data.length);
        }

        this.countriesPage.set(page);
      },
      error: (error) => {
        console.error('Error loading countries:', error);
      },
    });
  }

  onCountryPageChange(page: number): void {
    this.loadCountries(page);
  }

  loadAiOverview(page: number = 1): void {
    if (!this.isSectionActive(SeoStatsSectionId.AI_OVERVIEW)) return;

    // const fetchCtx = this.captureProductFetchContext();
    // if (!fetchCtx) return;

    const productId = this.apiService.getSelectedProductId();
    const params: any = {
      productId: productId,
      page,
      limit: this.itemsPerPage,
    };

    if (this.selectedTableDate()?.start) {
      params.date = dayjs(this.selectedTableDate()?.start).format('YYYY-MM');
    } else if (this.selectedTableYear()?.start) {
      params.date = this.selectedTableYear()?.start;
    } else if (this.selectedDateRange()?.start) {
      params.date = dayjs(this.selectedDateRange()?.start).format('YYYY-MM');
    }

    this.apiService.get('/seo/ai-overview-keywords', params).subscribe({
      next: (response: any) => {
        // if (this.isStaleProductFetch(fetchCtx)) return;
        let data = response.data || response.list || response || [];
        const pagination = response.pagination || (response.data && response.data.pagination);

        if (data && data.keywords) {
          data = data.keywords;
        }

        const mappedData = (Array.isArray(data) ? data : []).map(
          (item: any) => {
            const snapshotUrl = item.snapshotUrl || item.snapshotLink || '';
            return {
              ...item,
              snapshotLink: snapshotUrl ? `<a href="${snapshotUrl}" target="_blank" class="text-[#0066CC] hover:underline font-bold">View</a>` : '-',
              date: item.date,
            };
          }
        );
        this.aiOverviewData.set(mappedData);

        if (pagination && pagination.total !== undefined) {
          this.totalAiOverview.set(pagination.total);
        } else {
          this.totalAiOverview.set(data.length);
        }
        this.aiOverviewPage.set(page);
      },
      error: (error) => {
        console.error('Error loading AI Overview:', error);
      },
    });
  }

  onAiOverviewPageChange(page: number): void {
    this.loadAiOverview(page);
  }




  onTableDateFilter(dateValue: any): void {
    this.currentPage.set(1);
    this.landingPagesPage.set(1);
    this.citiesPage.set(1);
    this.countriesPage.set(1);
    this.reviewsPage.set(1);
    this.aiOverviewPage.set(1);
    this.detailedReviewsPage.set(1);

    if (dateValue && dateValue.year && dateValue.month) {
      const startDate = `${dateValue.year}-${String(dateValue.month).padStart(
        2,
        '0'
      )}-01`;
      const endDate = new Date(dateValue.year, dateValue.month, 0)
        .toISOString()
        .split('T')[0];
      this.selectedTableDate.set({ start: startDate, end: endDate });
      this.selectedTableYear.set(null);
    } else if (dateValue && dateValue.year) {
      this.selectedTableYear.set({ start: dateValue.year });
      this.selectedTableDate.set(null);
    } else {
      this.selectedTableDate.set(null);
      this.selectedTableYear.set(null);
    }

    // Auto-close logic similar to social-platform
    setTimeout(() => {
      if (this.dateDropdown) {
        this.dateDropdown.close();
      }
    }, 100);
  }

  clearTableFilters(): void {
    this.currentPage.set(1);
    this.landingPagesPage.set(1);
    this.citiesPage.set(1);
    this.countriesPage.set(1);
    this.reviewsPage.set(1);
    this.aiOverviewPage.set(1);
    this.detailedReviewsPage.set(1);

    this.selectedTableDate.set(null);
    this.selectedTableYear.set(null);
    if (this.dateDropdown) {
      this.dateDropdown.clearSelection();
    }
  }

  loadProducts(): void {
    if (this.productsList().length === 0) {
      this.apiService
        .get(
          '/product/list-product',
          {},
          {
            showErrorToaster: true,
            showSuccessToaster: false,
            showLoader: false,
            cache: true,
          }
        )
        .subscribe({
          next: (response: any) => {
            const filteredProducts = response
              .filter((item: any) => item.active)
              .map((item: any) => ({
                value: item.id,
                label: item.name,
              }));

            this.productsList.set(filteredProducts);
          },
          error: (error: any) =>
            console.error('Error fetching products:', error),
        });
    }
  }

  getProductOptions(): DropdownOption[] {
    return [{ value: '', label: 'Select Product' }, ...this.productsList()];
  }

  isInvalid(controlName: string): boolean {
    const tab = this.getTabForControl(controlName);
    if (tab && !this.hasTabContent(tab)) {
      return false;
    }

    const control = this.form.get(controlName);
    return !!(
      control &&
      control.invalid &&
      (control.touched || this.formSubmitted())
    );
  }

  getTabForControl(controlName: string): string | null {
    if (['clicks', 'impressions', 'activeVisitors', 'uniqueVisitors', 'traffic'].includes(controlName)) {
      return 'seo-metrics';
    }
    if (controlName.startsWith('landingRows.')) return 'landing';
    if (controlName.startsWith('cityRows.')) return 'cities';
    if (controlName.startsWith('ratingRows.')) return 'rating';
    if (controlName.startsWith('reviewRows.')) return 'reviews';
    return null;
  }

  hasTabContent(tab: string): boolean {
    if (tab === 'seo-metrics') return this.hasMetricsData();
    if (tab === 'landing') return this.hasArrayData(this.landingRows, ['landingUrl', 'landingClicks']);
    if (tab === 'cities') return this.hasArrayData(this.cityRows, ['cityName', 'cityUserCount']);
    if (tab === 'rating') return this.hasArrayData(this.ratingRows, ['ratingPlatform', 'ratingValue', 'ratingCount']);
    if (tab === 'reviews') return this.hasArrayData(this.reviewRows, ['reviewPlatform', 'reviewDate', 'reviewRating', 'reviewText', 'reviewerName', 'reviewerEmail']);
    return false;
  }

  hasTabError(tab: string): boolean {
    if (!this.hasTabContent(tab)) {
      return false;
    }

    if (this.isEditingMetric()) {
      const editingType = this.editingType();
      if (editingType === 'metrics' && tab === 'seo-metrics') {
        const metrics = ['clicks', 'impressions', 'activeVisitors', 'uniqueVisitors', 'traffic'];
        return metrics.some(m => this.isInvalid(m));
      }
      if (editingType === 'landing' && tab === 'landing') return this.landingRows.invalid && (this.landingRows.touched || this.formSubmitted());
      if (editingType === 'city' && tab === 'cities') return this.cityRows.invalid && (this.cityRows.touched || this.formSubmitted());
      if (editingType === 'rating' && tab === 'rating') return this.ratingRows.invalid && (this.ratingRows.touched || this.formSubmitted());
      if (editingType === 'review' && tab === 'reviews') return this.reviewRows.invalid && (this.reviewRows.touched || this.formSubmitted());
      if (editingType === 'ai-overview' && tab === 'ai-overview') return this.aiOverviewRows.invalid && (this.aiOverviewRows.touched || this.formSubmitted());
      return false;
    }

    // Bulk Add Mode
    if (tab === 'seo-metrics') {
      const metrics = ['clicks', 'impressions', 'activeVisitors', 'uniqueVisitors', 'traffic'];
      return metrics.some(m => this.isInvalid(m));
    }
    if (tab === 'landing') return this.landingRows.invalid && (this.landingRows.touched || this.formSubmitted());
    if (tab === 'cities') return this.cityRows.invalid && (this.cityRows.touched || this.formSubmitted());
    if (tab === 'rating') return this.ratingRows.invalid && (this.ratingRows.touched || this.formSubmitted());
    if (tab === 'reviews') return this.reviewRows.invalid && (this.reviewRows.touched || this.formSubmitted());

    return false;
  }

  getErrorMessage(controlName: string): string | undefined {
    const tab = this.getTabForControl(controlName);
    if (tab && !this.hasTabContent(tab)) {
      return undefined;
    }

    const control = this.form.get(controlName);
    if (!control || (!control.touched && !this.formSubmitted())) {
      return undefined;
    }

    if (control.hasError('required')) {
      return 'This field is required';
    }
    if (control.hasError('min')) {
      return 'Value must be greater than or equal to 0';
    }
    if (control.hasError('max')) {
      return 'Value must be 5 or less';
    }
    if (control.hasError('email')) {
      return 'Please enter a valid email address';
    }
    if (control.hasError('pattern')) {
      if (controlName.includes('landingClicks') || controlName.includes('clicks')) {
        return 'Value must be an integer';
      }
      return 'Please enter a valid URL (e.g., https://example.com)';
    }
    if (control.hasError('dateTooOld')) {
      return 'Year cannot be less than 2016';
    }
    if (control.hasError('dateInFuture')) {
      return 'Date cannot be in the future';
    }
    if (control.hasError('dateInvalid')) {
      return 'Please enter a valid date';
    }

    return undefined;
  }

  getLandingPageError(controlName: string): string | undefined {
    const control = this.landingPageForm.get(controlName);
    if (!control || !control.touched) {
      return undefined;
    }
    if (control.hasError('required')) {
      return 'This field is required';
    }
    if (control.hasError('pattern')) {
      return 'Please enter a valid URL';
    }
    if (control.hasError('min')) {
      return 'Value must be greater than or equal to 0';
    }
    return undefined;
  }

  getCityError(controlName: string): string | undefined {
    const control = this.cityForm.get(controlName);
    if (!control || !control.touched) {
      return undefined;
    }
    if (control.hasError('required')) {
      return 'This field is required';
    }
    if (control.hasError('min')) {
      return 'Value must be greater than or equal to 0';
    }
    return undefined;
  }

  getReviewError(controlName: string): string | undefined {
    const control = this.reviewForm.get(controlName);
    if (!control || !control.touched) {
      return undefined;
    }
    if (control.hasError('required')) {
      return 'This field is required';
    }
    if (control.hasError('min')) {
      return 'Value must be greater than or equal to 0';
    }
    if (control.hasError('max')) {
      return 'Rating must be 5 or less';
    }
    return undefined;
  }

  // Edit State
  editingType = signal<'metrics' | 'landing' | 'city' | 'rating' | 'review' | 'ai-overview' | 'country' | null>(null);
  editingId = signal<string | null>(null);

  openPanel(data: any = null, type: 'metrics' | 'landing' | 'city' | 'rating' | 'review' | 'ai-overview' | 'country' = 'metrics') {
    this.loadKeywords();
    this.loadAiKeywords();

    if (this.platformOptions().length === 0) {
      this.loadRatingPlatforms();
    }
    if (this.countryOptions().length === 0) {
      this.loadCountryOptions();
    }

    this.form.reset();
    this.formSubmitted.set(false);
    this.errorMessage.set('');
    this.successMessage.set('');
    this.activeStatsTab.set('seo-metrics');

    // Reset Form Arrays
    this.landingRows.clear();
    this.cityRows.clear();
    this.ratingRows.clear();
    this.reviewRows.clear();
    this.aiOverviewRows.clear();
    this.countryRows.clear();

    if (data) {
      this.isEditingMetric.set(true);
      this.editingType.set(type);
      this.editingId.set(data.pid || data.id || null);

      // Parse Date
      let year = null;
      let month = null;
      if (data.date) {
        const parts = data.date.split('-');
        if (parts.length >= 2) {
          year = parseInt(parts[0], 10);
          month = parseInt(parts[1], 10);
        }
      }

      const dateRef = { year, month };
      this.form.patchValue({ dateReference: dateRef });
      this.form.get('dateReference')?.disable();

      if (type === 'metrics') {
        this.activeStatsTab.set('seo-metrics');
        this.form.patchValue({
          clicks: data.clicks,
          impressions: data.impressions,
          activeVisitors: data.activeVisitors,
          uniqueVisitors: data.uniqueVisitors,
          traffic: data.traffic,
        });
        // Ensure arrays have at least one empty row even if not editing them
        this.addLandingRow();
        this.addCityRow();
        this.addReviewRow();
        this.addAiOverviewRow();
        this.addCountryRow();

      } else if (type === 'landing') {
        this.activeStatsTab.set('landing');
        const landingGroup = this.createLandingRowFormGroup();
        landingGroup.patchValue({
          landingUrl: data.keyword,
          landingClicks: data.rank,
        });
        this.landingRows.push(landingGroup);
        // Add empty rows for others
        this.addCityRow();
        this.addReviewRow();
        this.addAiOverviewRow();
        this.addCountryRow();

      } else if (type === 'city') {
        this.activeStatsTab.set('cities');
        const cityGroup = this.createCityRowFormGroup();
        cityGroup.patchValue({
          cityName: data.city,
          cityUserCount: data.userCount,
        });
        this.cityRows.push(cityGroup);
        // Add empty rows for others
        this.addLandingRow();
        this.addReviewRow();
        this.addAiOverviewRow();
        this.addCountryRow();

      } else if (type === 'rating') {
        this.activeStatsTab.set('rating');
        const ratingGroup = this.createRatingRowFormGroup();
        ratingGroup.patchValue({
          ratingPlatform: this.platformIdOf(data),
          ratingValue: data.rating,
          ratingCount: data.reviewsCount || data.review || data.count,
        });
        this.ratingRows.push(ratingGroup);
        // Add empty others
        this.addLandingRow();
        this.addCityRow();
        this.addReviewRow();
        this.addAiOverviewRow();
        this.addCountryRow();

      } else if (type === 'review') {
        this.activeStatsTab.set('reviews');
        // Detailed reviews edit not yet fully supported by single item modals, 
        // but adding row for consistency if panel is opened with review data
        const reviewGroup = this.createReviewRowFormGroup();
        reviewGroup.patchValue({
          reviewPlatform: this.platformIdOf(data),
          reviewDate: data.reviewDate,
          reviewRating: data.rating,
          reviewText: data.reviewText,
          reviewerName: data.reviewerName,
          reviewerEmail: data.reviewerEmail ?? '',
        });
        this.reviewRows.push(reviewGroup);
        // Add empty others
        this.addLandingRow();
        this.addCityRow();
        this.addRatingRow();
        this.addAiOverviewRow();
        this.addCountryRow();

      } else if (type === 'ai-overview') {
        this.activeStatsTab.set('ai-overview');
        const aiGroup = this.createAiOverviewRowFormGroup();

        let parsedSnapshotLink = data.snapshotUrl || data.snapshotLink || data.value || data.text || '';
        if (typeof parsedSnapshotLink === 'string' && parsedSnapshotLink.includes('<a href="')) {
          const match = parsedSnapshotLink.match(/href="([^"]*)"/);
          if (match && match[1]) {
            parsedSnapshotLink = match[1];
          }
        }

        aiGroup.patchValue({
          snapshotLink: parsedSnapshotLink,
          aiKeyword: data.keyword || data.label,
        });
        this.aiOverviewRows.push(aiGroup);
        // Add empty others
        this.addLandingRow();
        this.addCityRow();
        this.addRatingRow();
        this.addReviewRow();
        this.addCountryRow();
      } else if (type === 'country') {
        this.activeStatsTab.set('cities');
        const countryGroup = this.createCountryRowFormGroup();
        countryGroup.patchValue({
          country: data.countryId || data.country,
          countryImpressions: data.impressions,
          countryClicks: data.clicks,
        });
        this.countryRows.push(countryGroup);
        // Add empty others
        this.addLandingRow();
        this.addCityRow();
        this.addRatingRow();
        this.addReviewRow();
        this.addAiOverviewRow();
      }

    } else {
      // Add Mode
      this.isEditingMetric.set(false);
      this.editingType.set(null);
      this.editingId.set(null);
      this.form.get('dateReference')?.enable();

      const now = this.nowInLA();
      this.form.patchValue({
        dateReference: {
          year: now.year(),
          month: now.month() + 1,
        },
      });

      // Initialize with one row for each
      this.addLandingRow();
      this.addCityRow();
      this.addRatingRow();
      this.addReviewRow();
      this.addAiOverviewRow();
      this.addCountryRow();
    }
    this.panelOpen.set(true);
  }

  closePanel() {
    this.panelOpen.set(false);
    this.form.reset({
      product: '',
      dateReference: '',
      clicks: 0,
      impressions: 0,
      activeVisitors: 0,
      uniqueVisitors: 0,
      traffic: 0
    });
    this.form.get('dateReference')?.enable();

    // Reset Form Arrays
    this.landingRows.clear();
    this.cityRows.clear();
    this.ratingRows.clear();
    this.reviewRows.clear();
    this.addLandingRow();
    this.addCityRow();
    this.addRatingRow();
    this.addReviewRow();

    this.formSubmitted.set(false);
    this.isEditingMetric.set(false);
    this.editingType.set(null);
    this.editingId.set(null);
    this.errorMessage.set('');
    this.successMessage.set('');
  }

  // Table Action Options — metrics table (has delete)
  tableActionOptions = {
    showToggleButton: true,
    toggleOptions: [
      {
        label: 'Edit',
        value: 'edit',
        iconName: 'squarePen' as IconName,
        isShow: true,
        color: '#1E6AFD',
      },
      {
        label: 'Delete',
        value: 'delete',
        iconName: 'trash' as IconName,
        isShow: true,
        color: 'red',
      },
    ],
  };

  // Table actions with delete — for cities and ratings tables
  tableActionWithDelete = {
    showToggleButton: true,
    toggleOptions: [
      {
        label: 'Edit',
        value: 'edit',
        iconName: 'squarePen' as IconName,
        isShow: true,
        color: '#1E6AFD',
      },
      {
        label: 'Delete',
        value: 'delete',
        iconName: 'trash' as IconName,
        isShow: true,
        color: 'red',
      },
    ],
  };

  // Table actions without delete — for countries, landing pages, AI overview, detailed reviews
  tableActionNoDelete = {
    showToggleButton: true,
    toggleOptions: [
      {
        label: 'Edit',
        value: 'edit',
        iconName: 'squarePen' as IconName,
        isShow: true,
        color: '#1E6AFD',
      },
    ],
  };

  handleTableAction(event: { action: string; row: any }) {
    if (event.action === 'edit') {
      const row = event.row;

      // Parse date "YYYY-MM" -> { year: yyyy, month: mm }
      let dateRef = null;
      // Main SEO Stats table usually handles metrics. 
      // If other types are needed, they can be handled here.
      this.openPanel(event.row, 'metrics');
    } else if (event.action === 'delete') {
      this.openConfirmationDialog(event.row, 'metric');
    }
  }

  openConfirmationDialog(
    row: any,
    type:
      | 'metric'
      | 'landingPage'
      | 'city'
      | 'review'
      | 'rating'
      | 'country'
      | 'aiOverview'
  ) {
    const titleMap = {
      metric: 'SEO Metric',
      landingPage: 'Landing Page',
      city: 'City Performance',
      review: 'Review',
      rating: 'Rating',
      country: 'Country Stat',
      aiOverview: 'AI Overview Keyword',
    };

    this.dialog
      .open(ConfirmationModalComponent, {
        width: '400px',
        data: {
          title: 'Confirm Deletion',
          description: `Are you sure you want to delete this ${titleMap[type]}?`,
        },
      })
      .afterClosed()
      .subscribe((res) => {
        if (res) {
          switch (type) {
            case 'metric':
              this.deleteMetric(row.pid);
              break;
            case 'landingPage':
              this.deleteLandingPage(row.id);
              break;
            case 'city':
              this.deleteCity(row.pid);
              break;
            case 'rating':
              this.deleteReview(row.pid);
              break;
            case 'review':
              this.deleteDetailedReview(row.pid);
              break;
            case 'country':
              this.deleteCountry(row.id);
              break;
            case 'aiOverview':
              this.deleteAiOverview(row.id);
              break;
          }
        }
      });
  }

  refreshRecordsAfterAction() {
    this.loadTopRecords();
    this.auditLogPanel?.reloadLogs();
    if (this.view !== 'analyticsView') {
      this.loadLandingPages(this.landingPagesPage());
      this.loadCityUsers(this.citiesPage());
      this.loadAiOverview(this.aiOverviewPage());
      this.loadCountries(this.countriesPage());
      this.loadRatings(this.reviewsPage());
      this.loadDetailedReviews(this.detailedReviewsPage());
    }
  }

  deleteMetric(id: string) {
    this.apiService
      .delete(`/seo/metrics/${id}`, {
        showSuccessToaster: false,
      })
      .subscribe(() => {
        this.toasterService.success('SEO metric deleted successfully');
        this.loadSEOStats(this.currentPage());
        this.loadOverallPerformance();
        this.auditLogPanel?.reloadLogs();
      });
  }

  deleteLandingPage(id: string) {
    this.apiService
      .delete(`/seo/keyword-ranking/${id}`, {
        showSuccessToaster: false,
      })
      .subscribe(() => {
        this.toasterService.success('Landing page deleted successfully');
        this.refreshRecordsAfterAction();
      });
  }

  deleteCity(id: string) {
    this.apiService
      .delete(`/seo/city-users/${id}`, {
        showSuccessToaster: false,
      })
      .subscribe(() => {
        this.toasterService.success('City deleted successfully');
        this.refreshRecordsAfterAction();
      });
  }

  deleteReview(id: string) {
    this.apiService
      .delete(`/seo/ratings/${id}`, {
        showSuccessToaster: false,
      })
      .subscribe(() => {
        this.toasterService.success('Rating deleted successfully');
        this.refreshRecordsAfterAction();
      });
  }

  deleteDetailedReview(id: string) {
    this.apiService
      .delete(`/seo/reviews/${id}`, {
        showSuccessToaster: false,
      })
      .subscribe(() => {
        this.toasterService.success('Review deleted successfully');
        this.refreshRecordsAfterAction();
      });
  }

  deleteCountry(id: string) {
    this.apiService
      .delete(`/seo/country-stats/${id}`, {
        showSuccessToaster: false,
      })
      .subscribe(() => {
        this.toasterService.success('Country stat deleted successfully');
        this.refreshRecordsAfterAction();
      });
  }

  deleteAiOverview(id: string) {
    this.apiService
      .delete(`/seo/ai-overview-keywords/${id}`, {
        showSuccessToaster: false,
      })
      .subscribe(() => {
        this.toasterService.success('AI Overview keyword deleted successfully');
        this.refreshRecordsAfterAction();
      });
  }

  openLandingPageModal(page: any = null) {
    if (page) {
      this.currentLandingPageIndex = this.landingPages().findIndex(
        (p) => p === page
      );

      let month = null;
      let year = null;
      if (page.date) {
        const parts = page.date.split('-');
        if (parts.length >= 2) {
          year = String(parseInt(parts[0], 10));
          month = String(parseInt(parts[1], 10));
        }
      }

      this.landingPageForm.patchValue({
        url: page.url,
        clicks: page.clicks,
        month: month,
        year: year,
      });
      this.landingPageForm.get('url')?.disable();
      this.landingPageForm.get('month')?.disable();
      this.landingPageForm.get('year')?.disable();
    } else {
      this.currentLandingPageIndex = null;
      this.landingPageForm.get('url')?.enable();
      this.landingPageForm.get('month')?.enable();
      this.landingPageForm.get('year')?.enable();
      this.landingPageForm.reset({ clicks: 0, month: null, year: null });
    }
    this.landingPageSubmitted.set(false);
    this.isLandingPageModalOpen.set(true);
  }

  closeLandingPageModal() {
    this.isLandingPageModalOpen.set(false);
    this.currentLandingPageIndex = null;
    this.landingPageSubmitted.set(false);
    this.landingPageForm.get('url')?.enable();
    this.landingPageForm.get('month')?.enable();
    this.landingPageForm.get('year')?.enable();
  }

  saveLandingPage() {
    if (this.landingPageForm.valid) {
      const formValue = this.landingPageForm.getRawValue();
      const productId = this.apiService.getSelectedProductId();

      const payload = {
        productId,
        url: formValue.url,
        clicks: formValue.clicks,
        date: `${formValue.year}-${String(formValue.month).padStart(2, '0')}`,
      };

      this.apiService
        .post('/seo/landing-pages', payload, {
          showSuccessToaster: false,
        })
        .subscribe({
          next: () => {
            this.toasterService.success(
              this.currentLandingPageIndex !== null
                ? 'Landing page updated successfully'
                : 'Landing page added successfully'
            );
            this.closeLandingPageModal();
            this.refreshRecordsAfterAction();
          },
          error: (error) => {
            console.error('Error saving landing page:', error);
          },
        });
    } else {
      this.landingPageSubmitted.set(true);
    }
  }

  handleLandingPageTableAction(event: { action: string; row: any }) {
    if (event.action === 'edit') {
      this.openPanel(event.row, 'landing');
    } else if (event.action === 'delete') {
      this.openConfirmationDialog(event.row, 'landingPage');
    }
  }

  openCityModal(city: any = null) {
    if (city) {
      this.currentCityIndex = this.topCities().findIndex((c) => c === city);

      let month = null;
      let year = null;
      if (city.date) {
        const parts = city.date.split('-');
        if (parts.length >= 2) {
          year = String(parseInt(parts[0], 10));
          month = String(parseInt(parts[1], 10));
        }
      }

      this.cityForm.patchValue({
        city: city.city,
        newUsers: city.userCount,
        month: month,
        year: year,
      });
      this.cityForm.get('city')?.disable();
      this.cityForm.get('month')?.disable();
      this.cityForm.get('year')?.disable();
    } else {
      this.currentCityIndex = null;
      this.cityForm.get('city')?.enable();
      this.cityForm.get('month')?.enable();
      this.cityForm.get('year')?.enable();
      this.cityForm.reset({ newUsers: 0, month: null, year: null });
    }
    this.citySubmitted.set(false);
    this.isCityModalOpen.set(true);
  }

  closeCityModal() {
    this.isCityModalOpen.set(false);
    this.currentCityIndex = null;
    this.citySubmitted.set(false);
    this.cityForm.get('city')?.enable();
    this.cityForm.get('month')?.enable();
    this.cityForm.get('year')?.enable();
  }

  saveCity() {
    if (this.cityForm.valid) {
      const formValue = this.cityForm.getRawValue();
      const productId = this.apiService.getSelectedProductId();

      const payload = {
        productId,
        city: formValue.city,
        userCount: formValue.newUsers,
        date: `${formValue.year}-${String(formValue.month).padStart(2, '0')}`,
      };

      this.apiService
        .post('/seo/city-users', payload, {
          showSuccessToaster: false,
        })
        .subscribe({
          next: () => {
            this.toasterService.success(
              this.currentCityIndex !== null
                ? 'City updated successfully'
                : 'City added successfully'
            );
            this.closeCityModal();
            this.refreshRecordsAfterAction();
          },
          error: (error) => {
            console.error('Error saving city:', error);
          },
        });
    } else {
      this.citySubmitted.set(true);
    }
  }

  handleCityTableAction(event: { action: string; row: any }) {
    if (event.action === 'edit') {
      this.openPanel(event.row, 'city');
    } else if (event.action === 'delete') {
      this.openConfirmationDialog(event.row, 'city');
    }
  }

  handleCountryTableAction(event: { action: string; row: any }) {
    if (event.action === 'edit') {
      this.openPanel(event.row, 'country');
    } else if (event.action === 'delete') {
      this.openConfirmationDialog(event.row, 'country');
    }
  }

  handleAiOverviewTableAction(event: { action: string; row: any }) {
    if (event.action === 'edit') {
      this.openPanel(event.row, 'ai-overview');
    } else if (event.action === 'delete') {
      this.openConfirmationDialog(event.row, 'aiOverview');
    }
  }

  // Reviews Logic
  openReviewModal(review: any = null) {
    if (review) {
      this.currentReviewIndex = this.reviewsRatings().findIndex(
        (r) => r === review
      ); // Simple ref match or find by unique id if available

      let month = null;
      let year = null;
      if (review.date) {
        const parts = review.date.split('-');
        if (parts.length >= 2) {
          year = String(parseInt(parts[0], 10));
          month = String(parseInt(parts[1], 10));
        }
      }

      this.reviewForm.patchValue({
        platform: this.platformIdOf(review) || review.platform,
        rating: review.rating,
        reviewsCount: review.reviewsCount,
        month: month,
        year: year,
      });
      this.reviewForm.get('platform')?.disable();
      this.reviewForm.get('month')?.disable();
      this.reviewForm.get('year')?.disable();
    } else {
      this.currentReviewIndex = null;
      this.reviewForm.get('platform')?.enable();
      this.reviewForm.get('month')?.enable();
      this.reviewForm.get('year')?.enable();
      this.reviewForm.reset({
        rating: 0,
        reviewsCount: 0,
        month: null,
        year: null,
      });
    }
    this.reviewSubmitted.set(false);
    this.isReviewModalOpen.set(true);
  }

  closeReviewModal() {
    this.isReviewModalOpen.set(false);
    this.currentReviewIndex = null;
    this.reviewSubmitted.set(false);
    this.reviewForm.get('platform')?.enable();
    this.reviewForm.get('month')?.enable();
    this.reviewForm.get('year')?.enable();
  }

  saveReview() {
    if (this.reviewForm.valid) {
      const formValue = this.reviewForm.getRawValue();
      const productId = this.apiService.getSelectedProductId();

      const payload = {
        productId,
        platformId: formValue.platform,
        rating: formValue.rating,
        count: formValue.reviewsCount, // API likely expects 'count', mapping from UI 'reviewsCount'
        date: `${formValue.year} -${String(formValue.month).padStart(2, '0')} `,
      };

      this.apiService
        .post('/seo/ratings', payload, {
          showSuccessToaster: false,
        })
        .subscribe({
          next: () => {
            this.toasterService.success(
              this.currentReviewIndex !== null
                ? 'Review updated successfully'
                : 'Review added successfully'
            );
            this.closeReviewModal();
            this.refreshRecordsAfterAction();
          },
          error: (error) => {
            console.error('Error saving review:', error);
          },
        });
    } else {
      this.reviewSubmitted.set(true);
    }
  }

  onReviewPageChange(page: number) {
    this.loadRatings(page);
  }

  handleReviewTableAction(event: { action: string; row: any }) {
    if (event.action === 'edit') {
      this.openPanel(event.row, 'rating');
    } else if (event.action === 'delete') {
      this.openConfirmationDialog(event.row, 'rating');
    }
  }

  handleDetailedReviewTableAction(event: { action: string; row: any }) {
    if (event.action === 'edit') {
      this.openPanel(event.row, 'review');
    } else if (event.action === 'delete') {
      this.openConfirmationDialog(event.row, 'review');
    }
  }

  submitForm() {
    this.formSubmitted.set(true);

    // In Edit Mode, validate only the relevant section
    if (this.isEditingMetric() && this.editingType()) {
      const type = this.editingType();
      let isValid = true;

      if (type === 'metrics') {
        // Validate main form controls for metrics
        // Metrics fields are optional in the form definition usually, but let's check.
        // If they are required, we check specific controls.
        // Assuming 'clicks', 'impressions' etc are part of the main form.
        // We can just check the specific controls validity for now or skip if they aren't strictly required by Angular validators.
        // If the main form has errors on OTHER fields (like dateReference which is disabled), it might be an issue.
        // But dateReference is disabled, so it shouldn't cause invalid status if it has value.
        // Let's assume validity check is OK if we bypass the global check.
      } else if (type === 'landing') {
        if (this.landingRows.invalid) {
          return;
        }
      } else if (type === 'city') {
        if (this.cityRows.invalid) {
          return;
        }
      } else if (type === 'review') {
        if (this.reviewRows.invalid) {
          return;
        }
      } else if (type === 'ai-overview') {
        if (this.aiOverviewRows.invalid) {
          return;
        }
      }
    } else {
      // Bulk Add Mode - Check only sections with data
      let hasError = false;

      // Date is always required in Bulk Add
      if (this.form.get('dateReference')?.invalid) {
        hasError = true;
      }

      // If a section has data, it must be valid. 
      // If it's the active tab, we also force validation (optional, but good for UX)

      // Metrics validation
      if (this.hasMetricsData()) {
        const metrics = ['clicks', 'impressions', 'activeVisitors', 'uniqueVisitors', 'traffic'];
        metrics.forEach(m => {
          if (this.form.get(m)?.invalid) {
            hasError = true;
          }
        });
      }

      // Landing validation
      if (this.hasArrayData(this.landingRows, ['landingUrl', 'landingClicks'])) {
        if (this.landingRows.invalid) {
          hasError = true;
        }
      }

      // City validation
      if (this.hasArrayData(this.cityRows, ['cityName', 'cityUserCount'])) {
        if (this.cityRows.invalid) {
          hasError = true;
        }
      }

      // Rating validation
      if (this.hasArrayData(this.ratingRows, ['ratingPlatform', 'ratingValue', 'ratingCount'])) {
        if (this.ratingRows.invalid) {
          hasError = true;
        }
      }

      // Review validation
      if (this.hasArrayData(this.reviewRows, ['reviewPlatform', 'reviewDate', 'reviewRating', 'reviewText', 'reviewerName', 'reviewerEmail'])) {
        if (this.reviewRows.invalid) {
          hasError = true;
        }
      }

      // AI Overview validation
      if (this.hasArrayData(this.aiOverviewRows, ['snapshotLink', 'aiKeyword'])) {
        if (this.aiOverviewRows.invalid) {
          hasError = true;
        }
      }

      if (hasError) {
        return;
      }
    }

    this.isLoading.set(true);
    this.errorMessage.set('');
    this.successMessage.set('');

    const productId = this.apiService.getSelectedProductId();
    const formRawValue = this.form.getRawValue();

    // Format dateReference to YYYY-MM (Needed for both Create and Edit)
    let formattedDate = formRawValue.dateReference;
    if (formattedDate && typeof formattedDate === 'object' && formattedDate.year && formattedDate.month) {
      formattedDate = `${formattedDate.year}-${String(formattedDate.month).padStart(2, '0')}`;
    } else if (this.isEditingMetric() && this.editingType()) {
      // In edit mode, if dateReference is disabled, getRawValue might return the object or string.
      // form.getRawValue() includes disabled fields. 
      // Ensure logic holds.
      if (formattedDate && typeof formattedDate === 'object' && formattedDate.year && formattedDate.month) {
        formattedDate = `${formattedDate.year}-${String(formattedDate.month).padStart(2, '0')}`;
      }
    }


    if (this.isEditingMetric() && this.editingType()) {
      // HANDLE EDIT
      const type = this.editingType();
      const id = this.editingId(); // ID might be needed for URL or payload depending on API

      let updateObservable;
      const options = {
        showLoader: true,
        showErrorToaster: true,
        showSuccessToaster: false,
      };

      if (type === 'metrics') {
        const payload = {
          id, // from editingId()
          productId: productId,
          date: formattedDate,
          clicks: formRawValue.clicks ? (formRawValue.clicks) : null,
          impressions: formRawValue.impressions ? (formRawValue.impressions) : null,
          activeVisitors: formRawValue.activeVisitors ? (formRawValue.activeVisitors) : null,
          uniqueVisitors: formRawValue.uniqueVisitors ? (formRawValue.uniqueVisitors) : null,
          traffic: formRawValue.traffic ? (formRawValue.traffic) : null,
        };
        // User said: Edit API is .../seo/metrics
        updateObservable = this.apiService.post('/seo/metrics', payload, options);

      } else if (type === 'landing') {
        const row = this.landingRows.getRawValue()[0];
        const payload = {
          id,
          productId,
          date: formattedDate,
          keyword: row.landingUrl,
          rank: row.landingClicks,
        };
        updateObservable = this.apiService.post('/seo/keyword-ranking', payload, options);

      } else if (type === 'city') {
        const row = this.cityRows.getRawValue()[0];
        const payload = {
          id, // from editingId()
          productId,
          date: formattedDate,
          city: row.cityName,
          userCount: row.cityUserCount,
        };
        updateObservable = this.apiService.post('/seo/city-users', payload, options);

      } else if (type === 'rating') {
        const row = this.ratingRows.getRawValue()[0];
        const payload = {
          id, // from editingId()
          productId,
          date: formattedDate,
          platformId: row.ratingPlatform,
          rating: row.ratingValue,
          count: row.ratingCount,
        };
        updateObservable = this.apiService.post('/seo/ratings', payload, options);

      } else if (type === 'review') {
        const row = this.reviewRows.getRawValue()[0];
        const payload = {
          pid: id, // from editingId()
          productId,
          platformId: row.reviewPlatform,
          reviewDate: row.reviewDate,
          rating: row.reviewRating,
          reviewText: row.reviewText,
          reviewerName: row.reviewerName,
          // Write-only: GET /seo/reviews no longer returns it, and '' fails the email check.
          reviewerEmail: row.reviewerEmail || undefined,
        };
        // prism-new edits in place with PATCH (its POST always creates); the old API upserts on pid.
        const editUrl = `/seo/reviews/${id}`;
        updateObservable = apiBaseUrlFor('PATCH', editUrl) !== environment.apiUrl
          ? this.apiService.patch(editUrl, payload, options)
          : this.apiService.post('/seo/reviews', payload, options);

      } else if (type === 'ai-overview') {
        const row = this.aiOverviewRows.getRawValue()[0];
        const payload = {
          id,
          productId,
          date: formattedDate,
          keyword: row.aiKeyword,
          snapshotUrl: row.snapshotLink,
        };
        updateObservable = this.apiService.post('/seo/ai-overview-keywords', payload, options);
      } else if (type === 'country') {
        const row = this.countryRows.getRawValue()[0];
        const payload = {
          id, // from editingId()
          productId,
          date: formattedDate,
          countryId: row.country,
          impressions: row.countryImpressions,
          clicks: row.countryClicks,
        };
        updateObservable = this.apiService.post('/seo/country-stats', payload, options);
      }

      if (updateObservable) {
        updateObservable.subscribe({
          next: () => {
            this.toasterService.success('Record updated successfully');
            this.handleSuccessAction();
          },
          error: (error: any) => {
            this.handleErrorAction(error, 'Failed to update record');
          },
        });
      }

    } else {
      // HANDLE BULK CREATE (Refined Logic)
      const keywordRankingData = this.landingRows.getRawValue().map((row: any) => ({
        keyword: row.landingUrl,
        rank: row.landingClicks ? (row.landingClicks) : null,
      })).filter((r: any) => r.keyword || r.rank !== null);

      const cityData = this.cityRows.getRawValue().map((row: any) => ({
        city: row.cityName,
        userCount: row.cityUserCount ? (row.cityUserCount) : null,
      })).filter((r: any) => r.city || r.userCount !== null);

      const ratingData = this.ratingRows.getRawValue().map((row: any) => ({
        ratingPlatformId: row.ratingPlatform,
        ratingPlatformPid: this.platformPids.get(String(row.ratingPlatform)),
        rating: row.ratingValue,
        review: row.ratingCount,
      })).filter((r: any) => r.ratingPlatformId || r.rating);

      const reviewData = this.reviewRows.getRawValue().map((row: any) => ({
        platformId: row.reviewPlatform,
        platformPid: this.platformPids.get(String(row.reviewPlatform)),
        reviewDate: row.reviewDate,
        rating: row.reviewRating,
        reviewText: row.reviewText,
        reviewerName: row.reviewerName,
        reviewerEmail: row.reviewerEmail || undefined,
      })).filter((r: any) => r.platformId || r.reviewText);

      const aiOverviewData = this.aiOverviewRows.getRawValue().map((row: any) => ({
        snapshotUrl: row.snapshotLink,
        keyword: row.aiKeyword,
      })).filter((r: any) => r.snapshotUrl || r.keyword);

      const payload = {
        productId: productId,
        date: formattedDate,
        metrics: this.hasMetricsData() ? {
          clicks: formRawValue.clicks,
          impressions: formRawValue.impressions,
          activeVisitors: formRawValue.activeVisitors,
          uniqueVisitors: formRawValue.uniqueVisitors,
          traffic: formRawValue.traffic,
        } : null,
        keywordRanking: keywordRankingData.length > 0 ? keywordRankingData : null,
        cityAndUsers: cityData.length > 0 ? cityData : null,
        ratings: ratingData.length > 0 ? ratingData : null,
        reviews: reviewData.length > 0 ? reviewData : null,
        aiOverviewKeywords: aiOverviewData.length > 0 ? aiOverviewData : null,
        countryStats: (() => {
          const data = this.countryRows.getRawValue().map((row: any) => ({
            countryId: row.country,
            impressions: row.countryImpressions,
            clicks: row.countryClicks,
          })).filter((r: any) => r.countryId || r.impressions || r.clicks);
          return data.length > 0 ? data : null;
        })(),
      };

      this.apiService
        .post('/seo/create', payload, {
          showLoader: true,
          showErrorToaster: true,
          showSuccessToaster: false,
        })
        .subscribe({
          next: (response: any) => {
            this.toasterService.success('SEO stats added successfully');
            this.handleSuccessAction();
          },
          error: (error: any) => {
            this.handleErrorAction(error, 'Failed to add SEO stats');
          },
        });
    }
  }

  handleSuccessAction() {
    this.isLoading.set(false);
    setTimeout(() => {
      this.closePanel();
      this.auditLogPanel?.reloadLogs();
      const activeTab = this.activeStatsTab();
      if (activeTab === 'seo-metrics') {
        this.loadOverallPerformance();
        this.loadSEOStats();
      } else if (activeTab === 'landing') {
        this.loadLandingPages(this.landingPagesPage());
      } else if (activeTab === 'cities') {
        this.loadCityUsers(this.citiesPage());
        this.loadCountries(this.countriesPage());
        this.loadAiOverview(this.aiOverviewPage());
        this.loadTopRecords();
      } else if (activeTab === 'ai-overview' || activeTab === 'rating' || activeTab === 'reviews') {
        this.loadAiOverview(this.aiOverviewPage());
        this.loadRatings(this.reviewsPage());
        this.loadDetailedReviews(this.detailedReviewsPage());
        this.loadTopRecords();
      }
    }, 1000);
  }

  handleErrorAction(error: any, msg: string) {
    this.isLoading.set(false);
    this.errorMessage.set(error?.message || error?.error || msg);
    console.error(msg, error);
  }
}


